/* eslint-disable no-console */
import config from "../config";
import {
  AcquisitionReport,
  AcquisitionWindow
} from "@/interfaces/OfficeAcquisition.interfaces";
import { EducationUpdate } from "@/helpers/educationCredentials";
import { MembershipHistoryPage } from "@/interfaces/MembershipHistory";
import { sessionCheck } from "@/auth/client";
import {
  updateHireJobInterface,
  updateHygienistCancelledInterface
} from "../interfaces/HireJob.interfaces";
import {
  getPaymentIntegritiesInterface,
  sendOfficeFollowUpInterface,
  updareInvoiceInterface,
  sendHygAsstResponseInterface
} from "../interfaces/PaymentIntegrities.interfaces";
import {
  addLoggingInterface,
  searchAdminLoggingInterface
} from "../interfaces/AdminLogging.interfaces";
import { updateResumeURLInterface } from "../interfaces/Resume.interfaces";
import { dentistCancelledIfDoneInterface } from "../interfaces/DentistCancelled.interfaces";
import { shiftsCancelledBillingInterface } from "../interfaces/SNCBillingBoard.interfaces";
import {
  courseDisputeAnalyzer,
  takeTwoMonthBreakInterface
} from "../interfaces/DisputeAnalyzer.interfaces";
import { moveTwoPFInterface } from "../interfaces/SNCbyHygDa.interfaces";
import { updatePF } from "../interfaces/Tables.interfaces";
import { paginateTable } from "../interfaces/Additional.interfaces";
import {
  ensureOkResponse,
  parseRequiredJsonResponse
} from "@/helpers/apiResponse";
import {
  CreateSupportAssistantConversationResponse,
  SendSupportAssistantMessageResponse,
  SupportAssistantConversationListResponse,
  SupportAssistantConversationResponse
} from "@/interfaces/SupportAssistant.interfaces";

import {
  UpdateTablePayload,
  UpdateTableResponse,
  UpdateHygReviewedPayload,
  UpdateHygBlockedPayload,
  UpdateHygHoldPayload,
  UpdateFirstBookedStatusPayload,
  UpdateCancelledStatusesPayload,
  ChatHistoryResponse,
  S3UploadCredentials,
  PasswordResetResponse,
  AdminLoginResponse,
  TableInfoResponse,
  AdminReactivateUserPayload,
  AdminReactivateUserResponse
} from "../types/api.types";
import {
  BucketEmailDetail,
  BucketEmailHealth,
  BucketEmailListParams,
  BucketEmailListResponse
} from "@/interfaces/BucketEmail.interfaces";
import { ClaimPaymentResult } from "@/helpers/claimPaymentOutcome";
import { AutomaticPayoutRetryResult } from "@/helpers/paymentLifecycleAdminGuard";
import {
  AdminOAuthLoginError,
  adminOAuthFailureFromResponse
} from "@/auth/adminOAuthError";

/** TempStars API admin JWT (HS256, isAdmin + id). Separate from Cognito id token. */
export const ADMIN_API_JWT_KEY = "adminApiJwt";

/** Persist admin API JWT; keeps router guard (`jwt`) in sync. */
export const persistAdminApiJwt = (token: string): void => {
  const cleanJWT = token.replace(/^["']|["']$/g, "");
  localStorage.setItem(ADMIN_API_JWT_KEY, cleanJWT);
  localStorage.setItem("jwt", cleanJWT);
};

const adminApiJwtHeadersFromRaw = (raw: string | null): Record<string, string> | null => {
  if (!raw) {
    return null;
  }
  const cleanJWT = raw.replace(/^["']|["']$/g, "");
  try {
    const payload = JSON.parse(atob(cleanJWT.split(".")[1]));
    const isAdmin =
      payload.isAdmin === true || payload.isAdmin === 1 || payload.isAdmin === 'true';
    const adminId = payload.id;
    if (!isAdmin || adminId === null || adminId === undefined || adminId === '') {
      return null;
    }
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      return null;
    }
    return { Authorization: `Bearer ${cleanJWT}` };
  } catch {
    return null;
  }
};

/** Admin API JWT from login/oauth (HS256, isAdmin + id). Not the Cognito id token. */
const tryAdminApiJwtFromStorage = (): Record<string, string> | null => {
  return (
    adminApiJwtHeadersFromRaw(localStorage.getItem(ADMIN_API_JWT_KEY)) ||
    adminApiJwtHeadersFromRaw(localStorage.getItem("jwt"))
  );
};

/**
 * Headers for /v3/admin/* mutations (uploadImage, etc.). Never sends Cognito id token.
 * Exchanges cognitoIdToken via oauth-login when needed.
 */
export const ensureAdminApiJwtHeaders = async (): Promise<Record<string, string>> => {
  const existing = tryAdminApiJwtFromStorage();
  if (existing) {
    return existing;
  }

  const cognitoIdToken = localStorage.getItem("cognitoIdToken");
  if (cognitoIdToken) {
    console.log("🔑 No admin API JWT in storage — exchanging Cognito token via oauth-login");
    const oauthLogin = await adminLoginWithIdToken(cognitoIdToken);
    if (oauthLogin?.token) {
      persistAdminApiJwt(oauthLogin.token);
      const headers = tryAdminApiJwtFromStorage();
      if (headers) {
        return headers;
      }
    }
  }

  throw new Error(
    "Admin API session required. Log out, then sign in with Admin Login (username + password) or complete Google login."
  );
};

export const getOfficeAcquisitionReport = async (
  window: AcquisitionWindow
): Promise<AcquisitionReport> => {
  if (getCurrentDatabaseSource() === DATABASE_SOURCES.PROD_REPLICA) {
    throw new Error(
      "Select the default database to open the acquisition report."
    );
  }
  const headers = await ensureAdminApiJwtHeaders();
  const query = new URLSearchParams({ ...window });
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/analytics/office-attribution/view?${query}`,
    {
      method: "GET",
      headers,
      cache: "no-store"
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.error || "The report could not be loaded. Try again."
    );
  }
  return response.json();
};

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  // /v3/admin/* requires the API admin JWT (isAdmin + id). Cognito id tokens
  // pass masterpass (no id check) but fail uploadImage with 401.
  const adminApiHeaders = tryAdminApiJwtFromStorage();
  if (adminApiHeaders) {
    return adminApiHeaders;
  }

  try {
    const session = await sessionCheck();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      console.log("🔑 Using Amplify ID token for authentication");
      console.log("🔑 Token preview:", idToken.substring(0, 50) + "...");
      return {
        Authorization: `Bearer ${idToken}`
      };
    } else {
      console.warn("🔑 No Amplify ID token found.");

      // Fallback to localStorage JWT (for development bypass)
      const localJWT = localStorage.getItem("jwt");
      if (localJWT) {
        console.log("🔑 Using localStorage JWT for authentication");
        console.log("🔑 Local JWT preview:", localJWT.substring(0, 50) + "...");

        // Decode and validate the JWT
        try {
          const payload = JSON.parse(atob(localJWT.split(".")[1]));
          console.log("🔑 JWT payload:", payload);
          console.log("🔑 JWT has isAdmin?:", payload.isAdmin);
          console.log("🔑 JWT user ID:", payload.id);
          console.log(
            "🔑 JWT expires:",
            payload.exp ? new Date(payload.exp * 1000) : "No expiration"
          );
          console.log(
            "🔑 JWT issued:",
            payload.iat ? new Date(payload.iat * 1000) : "No issued time"
          );
          console.log("🔑 Current time:", new Date());

          // Check if this is an admin token
          if (!payload.isAdmin) {
            console.error(
              "🔑 JWT token is not an admin token! isAdmin field is missing or false"
            );
            console.error("🔑 This token cannot access admin endpoints");
          }

          // Check if token is expired
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            console.error("🔑 JWT token is expired! Clearing localStorage...");
            localStorage.clear();
            // Redirect to login
            if (typeof window !== "undefined") {
              window.location.href = "/login";
            }
            return {};
          }

          // If no expiration field, check if token is too old (older than 30 days)
          if (
            !payload.exp &&
            payload.iat &&
            payload.iat * 1000 < Date.now() - 30 * 24 * 60 * 60 * 1000
          ) {
            console.error(
              "🔑 JWT token is too old (no expiration field)! Clearing localStorage..."
            );
            localStorage.clear();
            // Redirect to login
            if (typeof window !== "undefined") {
              window.location.href = "/login";
            }
            return {};
          }
        } catch (decodeError) {
          console.error("🔑 Error decoding JWT:", decodeError);
        }

        // Remove any quotes that might be around the JWT
        const cleanJWT = localJWT.replace(/^["']|["']$/g, "");
        return {
          Authorization: `Bearer ${cleanJWT}`
        };
      }

      console.error("🔑 No authentication token found!");
      return {};
    }
  } catch (error) {
    console.error("🔑 Error fetching Amplify session for headers:", error);

    // Fallback to localStorage JWT (for development bypass)
    const localJWT = localStorage.getItem("jwt");
    if (localJWT) {
      console.log("🔑 Using localStorage JWT for authentication (fallback)");
      console.log(
        "🔑 Fallback JWT preview:",
        localJWT.substring(0, 50) + "..."
      );
      return {
        Authorization: `Bearer ${localJWT}`
      };
    }

    console.error("🔑 No fallback authentication available!");
    return {};
  }
};

// Database source constants
export const DATABASE_SOURCES = {
  STAGING: 'staging',
  PROD_REPLICA: 'prod-replica'
} as const;

export type DatabaseSource = typeof DATABASE_SOURCES[keyof typeof DATABASE_SOURCES];

/**
 * Gets the current database source from localStorage
 * Defaults to 'staging' if not set
 */
export const getCurrentDatabaseSource = (): DatabaseSource => {
  const source = localStorage.getItem("tempstars_database_source");
  if (source === DATABASE_SOURCES.PROD_REPLICA) {
    return DATABASE_SOURCES.PROD_REPLICA;
  }
  return DATABASE_SOURCES.STAGING;
};

/**
 * Sets the database source in localStorage
 * Only 'staging' and 'prod-replica' are valid values
 */
export const setDatabaseSource = (source: DatabaseSource): void => {
  if (source === DATABASE_SOURCES.PROD_REPLICA || source === DATABASE_SOURCES.STAGING) {
    localStorage.setItem("tempstars_database_source", source);
    // Dispatch event so UI components can react to the change
    window.dispatchEvent(new CustomEvent('database-source-changed', { detail: source }));
  }
};

// Wrapper around fetch to automatically add auth headers
export const fetchWithAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const authHeaders = await getAuthHeaders();

  // Log environment info for debugging
  const currentEnv = localStorage.getItem("tempstars_environment") || "staging";
  const finalUrl = url.startsWith("http")
    ? url
    : `${config.api_url}${url.replace(/^\//, "")}`;
  
  // Get current database source for header
  const databaseSource = getCurrentDatabaseSource();

  let baseHeaders: Record<string, string> = {}; // Initialize as Record<string, string>
  // Default to JSON content type for POST/PUT if not specified AND body exists
  if (
    (options.method === "POST" || options.method === "PUT") &&
    options.body &&
    (!options.headers || !options.headers["Content-Type"])
  ) {
    baseHeaders["Content-Type"] = "application/json";
  }
  
  // Add database source header if using prod-replica
  if (databaseSource === DATABASE_SOURCES.PROD_REPLICA) {
    baseHeaders["X-Database-Source"] = databaseSource;
  }

  // Merge headers: Start with base, add auth, then add specific call headers
  const mergedHeaders = {
    ...baseHeaders,
    ...authHeaders,
    ...(options.headers as Record<string, string> | undefined) // Cast to allow merging
  };

  // Clean headers - remove undefined values (though TS should prevent this now)
  Object.keys(mergedHeaders).forEach(
    key => mergedHeaders[key] === undefined && delete mergedHeaders[key]
  );

  const response = await fetch(finalUrl, {
    ...options,
    headers: mergedHeaders as HeadersInit // Assert type after cleaning
  });

  console.log("🌐 Response status:", response.status);
  console.log("🌐 Response ok:", response.ok);

  // Handle 401 Unauthorized with detailed logging
  if (response.status === 401) {
    console.error("❌ 401 Unauthorized - Authentication failed!");
    console.error("❌ Request URL:", url);
    console.error(
      "❌ Authorization header present?:",
      !!(mergedHeaders as any).Authorization
    );
    console.error(
      "❌ Authorization header format:",
      (mergedHeaders as any).Authorization ? "Bearer token" : "None"
    );

    try {
      const errorBody = await response.clone().json();
      console.error("❌ Server error response:", errorBody);
      console.error("❌ Error details:", JSON.stringify(errorBody, null, 2));

      // Check for specific JWT errors
      if (errorBody.error) {
        console.error("❌ Error object:", errorBody.error);
        if (errorBody.error.message) {
          console.error("❌ Error message:", errorBody.error.message);
        }
        if (errorBody.error.name) {
          console.error("❌ Error name:", errorBody.error.name);
        }
      }
    } catch (e) {
      try {
        const errorText = await response.clone().text();
        console.error("❌ Server error text:", errorText);
      } catch (e2) {
        console.error("❌ Could not read error response");
      }
    }

    // Check if JWT is valid
    const localJWT = localStorage.getItem("jwt");
    if (localJWT) {
      try {
        const payload = JSON.parse(atob(localJWT.split(".")[1]));
        console.error("❌ JWT payload at time of 401:", {
          id: payload.id,
          isAdmin: payload.isAdmin,
          exp: payload.exp,
          iat: payload.iat,
          dentistId: payload.dentistId,
          hygienistId: payload.hygienistId
        });
      } catch (e) {
        console.error("❌ Could not decode JWT");
      }
    } else {
      console.error("❌ No JWT found in localStorage!");
    }
  }

  return response;
};

/**
 * Admin login function - authenticates with staging/production API
 * @param username Admin username
 * @param password Admin password
 * @returns JWT token and admin data
 */
export const adminLogin = async (
  username: string,
  password: string
): Promise<AdminLoginResponse & { isAgent: boolean }> => {
  console.log("🔑 Admin Login: Attempting login for:", username);

  try {
    const response = await fetch(`${config.api_url}v3/admin/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ username, password })
    });

    console.log("🔑 Admin Login: Response status:", response.status);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Invalid username or password");
      }
      throw new Error(`Login failed with status: ${response.status}`);
    }

    const data = await response.json();
    console.log("🔑 Admin Login: Success! Token received");
    console.log("🔑 Admin Login: Admin data:", data.admin);

    return data;
  } catch (error) {
    console.error("🔑 Admin Login: Error:", error);
    throw error;
  }
};

/**
 * Admin password reset function - allows admin to reset any user's password
 * @param userId User ID to reset password for
 * @returns Password reset result with temporary password
 */
export const adminResetUserPassword = async (
  userId: number,
  password?: string
): Promise<PasswordResetResponse> => {
  console.log(
    "🔐 Admin Password Reset: Attempting to reset password for user:",
    userId
  );

  try {
    const token = localStorage.getItem("jwt");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const body: any = { userId };
    if (password) {
      body.password = password;
    }

    const response = await fetch(
      `${config.api_url}v3/admin/resetUserPassword`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      }
    );

    console.log("🔐 Admin Password Reset: Response status:", response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw new Error("Unauthorized - please log in again");
      } else if (response.status === 404) {
        throw new Error("User not found");
      } else if (response.status === 400) {
        throw new Error(errorData.message || "Invalid user ID");
      }
      throw new Error(`Password reset failed with status: ${response.status}`);
    }

    const data = await response.json();
    console.log("🔐 Admin Password Reset: Success!", data);

    return data;
  } catch (error) {
    console.error("🔐 Admin Password Reset: Error:", error);
    throw error;
  }
};

export const adminUnsubscribeUser = async (
  userId: number
): Promise<{
  success: boolean;
  message: string;
  userEmail: string;
  userId: number;
  mailChimpUnsubscribed: boolean;
}> => {
  console.log(
    "📧 Admin Unsubscribe: Attempting to unsubscribe user:",
    userId
  );

  try {
    const token = localStorage.getItem("jwt");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(
      `${config.api_url}v3/admin/unsubscribeUser`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      }
    );

    console.log("📧 Admin Unsubscribe: Response status:", response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw new Error("Unauthorized - please log in again");
      } else if (response.status === 404) {
        throw new Error("User not found");
      } else if (response.status === 400) {
        throw new Error(errorData.message || "Invalid user ID or no email address");
      }
      throw new Error(`Unsubscribe failed with status: ${response.status}`);
    }

    const data = await response.json();
    console.log("📧 Admin Unsubscribe: Success!", data);

    return data;
  } catch (error) {
    console.error("📧 Admin Unsubscribe: Error:", error);
    throw error;
  }
};

/**
 * Reactivate a deactivated TempStars user account (admin-only).
 *
 * Accepts EITHER `hygienistId` (talent) or `dentistId` (dental office).
 * The legacy single-argument signature (`adminReactivateUser(42)`) is
 * preserved for existing call sites so they keep compiling.
 *
 * T2-5692 added dental-office support. Payload/response types live in
 * `src/types/api.types.ts`.
 */
export const adminReactivateUser = async (
  idOrOptions: number | AdminReactivateUserPayload
): Promise<AdminReactivateUserResponse> => {
  try {
    const token = localStorage.getItem("jwt");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const payload: AdminReactivateUserPayload =
      typeof idOrOptions === "number"
        ? { hygienistId: idOrOptions }
        : {
            ...(idOrOptions.hygienistId != null
              ? { hygienistId: idOrOptions.hygienistId }
              : {}),
            ...(idOrOptions.dentistId != null
              ? { dentistId: idOrOptions.dentistId }
              : {}),
          };

    const response = await fetch(
      `${config.api_url}v3/admin/reactivateUser`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw new Error("Unauthorized - please log in again");
      } else if (response.status === 404) {
        throw new Error(
          payload.dentistId
            ? "No user found for this dentist"
            : "No user found for this hygienist"
        );
      } else if (response.status === 400) {
        throw new Error(errorData.message || "Account is not deactivated or invalid");
      }
      throw new Error(`Reactivation failed with status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Admin Reactivate User Error:", error);
    throw error;
  }
};

/**
 * Recursively cleans data by converting empty strings to null for database compatibility
 * This prevents MySQL DATETIME errors when empty strings are passed to date fields
 */
const cleanDataForDatabase = (data: any): any => {
  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data === "string") {
    // Convert empty strings to null, trim whitespace
    const trimmed = data.trim();
    return trimmed === "" ? null : trimmed;
  }

  if (typeof data === "number") {
    // Handle NaN and convert 0 to null for ID fields if needed
    return isNaN(data) ? null : data;
  }

  if (Array.isArray(data)) {
    return data.map(item => cleanDataForDatabase(item));
  }

  if (typeof data === "object") {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(data)) {
      // Special handling for ID fields - empty strings become null
      if (
        key.toLowerCase().includes("id") &&
        (value === "" || value === "0" || value === 0)
      ) {
        cleaned[key] = null;
      } else {
        cleaned[key] = cleanDataForDatabase(value);
      }
    }
    return cleaned;
  }

  return data;
};

/**
 * Validates and cleans search parameters for dispute data
 */
const validateDisputeSearchData = (data: any) => {
  // Ensure we have a proper structure
  const sanitized = {
    hygienist: {
      firstName: data?.hygienist?.firstName || "",
      lastName: data?.hygienist?.lastName || "",
      email: data?.hygienist?.email || "",
      id: data?.hygienist?.id || null
    },
    location: {
      name: data?.location?.name || "",
      address: data?.location?.address || "",
      email: data?.location?.email || "",
      id: data?.location?.id || null
    },
    jobId: data?.jobId || null
  };

  // Apply comprehensive cleaning
  return cleanDataForDatabase(sanitized);
};

/**
 * Enhanced error handler for database DATETIME corruption issues
 * Provides specific guidance for working around data corruption problems
 */
const handleDatabaseError = (cleanedData: any, errorData: any) => {
  const isDateTimeError =
    errorData?.error?.message?.includes("DATETIME") ||
    errorData?.message?.includes("DATETIME") ||
    errorData?.error?.message?.includes("ER_WRONG_VALUE");

  if (!isDateTimeError) {
    return null; // Not a datetime error, handle normally
  }

  // Build a detailed error message with workarounds
  let searchCriteria: string[] = [];
  if (cleanedData.hygienist?.firstName)
    searchCriteria.push(
      `• Hygienist First Name: "${cleanedData.hygienist.firstName}"`
    );
  if (cleanedData.hygienist?.lastName)
    searchCriteria.push(
      `• Hygienist Last Name: "${cleanedData.hygienist.lastName}"`
    );
  if (cleanedData.hygienist?.email)
    searchCriteria.push(`• Hygienist Email: "${cleanedData.hygienist.email}"`);
  if (cleanedData.hygienist?.id)
    searchCriteria.push(`• Hygienist ID: ${cleanedData.hygienist.id}`);
  if (cleanedData.location?.name)
    searchCriteria.push(`• Location Name: "${cleanedData.location.name}"`);
  if (cleanedData.location?.address)
    searchCriteria.push(
      `• Location Address: "${cleanedData.location.address}"`
    );
  if (cleanedData.location?.email)
    searchCriteria.push(`• Location Email: "${cleanedData.location.email}"`);
  if (cleanedData.location?.id)
    searchCriteria.push(`• Location ID: ${cleanedData.location.id}`);
  if (cleanedData.jobId) searchCriteria.push(`• Job ID: ${cleanedData.jobId}`);

  const searchDetails =
    searchCriteria.length > 0
      ? `\n\nYour search criteria:\n${searchCriteria.join("\n")}`
      : "";

  const workaroundSuggestions = `

🔧 WORKAROUND OPTIONS:

1. Try using more specific search criteria:
   - Use exact Hygienist ID or Location ID if known
   - Try searching with just the name fields first
   - Use email addresses for more precise matching

2. Alternative search approaches:
   - Search in the main tables (Hygienist, Location, Job) instead
   - Use the Table View with filters
   - Check the History section for recent activities

3. For immediate assistance:
   - Contact system administrator about database integrity
   - Use other admin dashboard sections that may not be affected
   - Try searching for the same data using different criteria

📋 TECHNICAL DETAILS:
Error: Database contains empty strings in DATETIME fields instead of NULL values
Impact: Prevents dispute search queries from executing properly
Status: This is a known database integrity issue requiring administrative action`;

  const fullMessage = `⚠️ DATABASE SEARCH ERROR

The search could not be completed due to corrupted date entries in the database. Your search criteria is valid, but some database records contain invalid date values that prevent the query from running.${searchDetails}${workaroundSuggestions}`;

  return fullMessage;
};

export const getTableInfo = async (tablename: string): Promise<any[]> => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/${tablename.toLowerCase()}`
    );
    
    if (!req.ok) {
      const errorText = await req.text().catch(() => req.statusText);
      console.error(`getTableInfo: API error for ${tablename}:`, {
        status: req.status,
        statusText: req.statusText,
        error: errorText
      });
      throw new Error(`Failed to fetch ${tablename}: ${req.status} ${req.statusText}`);
    }
    
    const data = await req.json();
    
    if (!Array.isArray(data)) {
      console.error(`getTableInfo: Response for ${tablename} is not an array:`, data);
      return [];
    }
    
    return data;
  } catch (error) {
    console.error(`getTableInfo: Error fetching ${tablename}:`, error);
    throw error;
  }
};

export const updateTable = async (data: UpdateTablePayload): Promise<any> => {
  // Clean the data before sending to prevent DATETIME errors
  const cleanedData = cleanDataForDatabase(data);

  console.log("🔍 updateTable: Original data:", data);
  console.log("🔍 updateTable: Cleaned data:", cleanedData);

  let req = await fetchWithAuth(`${config.api_url}v3/admin/update`, {
    method: "POST",
    body: JSON.stringify(cleanedData) // Stringify before passing
  });

  if (!req.ok) {
    const errorData = await req
      .json()
      .catch(() => ({ message: req.statusText }));
    console.error("🔍 updateTable: API Error:", {
      status: req.status,
      errorData: errorData,
      sentData: cleanedData
    });

    // Provide user-friendly error messages
    let userMessage = "An error occurred while updating the table.";
    const correctionCodes = [
      "ADMIN_INVOICE_ADMIN_REQUIRED",
      "ADMIN_INVOICE_HOURS_INVALID",
      "ADMIN_INVOICE_HOURS_STALE",
      "ADMIN_INVOICE_PAYMENT_UNRESOLVED",
      "ADMIN_INVOICE_TOPUP_REVIEW_REQUIRED"
    ];
    if (
      correctionCodes.includes(errorData?.data?.code) &&
      typeof errorData?.message === "string"
    ) {
      userMessage = errorData.message;
    }
    if (
      errorData?.error?.message?.includes("DATETIME") ||
      errorData?.message?.includes("DATETIME") ||
      errorData?.error?.message?.includes("ER_WRONG_VALUE")
    ) {
      userMessage =
        "Invalid date format detected. Please check your input and try again.";
    }

    throw new Error(userMessage);
  }

  return await req.json();
};

export interface SignupReview {
  signals: Array<{ id: number; signalType: string; detail?: unknown }>;
  duplicates: Array<{ id: number; duplicateHygienistId: number }>;
  mode: "off" | "monitor" | "enforce";
  requiresOverride: boolean;
  version: string;
  warningsUnavailable?: boolean;
}
export const getSignupReview = async (id: number): Promise<SignupReview> => {
  const response = await fetchWithAuth(`${config.api_url}v3/admin/hygienist/${id}/signup-review`, { method: "GET" });
  return parseRequiredJsonResponse<SignupReview>(response, "Failed to load account warnings");
};

export const updateHygReviewed = async (data: UpdateHygReviewedPayload): Promise<any> => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/updateHygReviewed`, {
    method: "POST",
    body: JSON.stringify(data) // Stringify
  });
  return ensureOkResponse(req, "Failed to approve Talent");
};

export const updateFirstBookedStatus = async (data: UpdateFirstBookedStatusPayload): Promise<any> => {
  let req = await fetchWithAuth(
    `${config.api_url}v3/admin/updateFirstBookedStatus`,
    {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    }
  );
  if (data.status === 3) {
    return await req.json();
  }
  return await req;
};

export const updateCancelledStatuses = async (data: UpdateCancelledStatusesPayload): Promise<any> => {
  let req = await fetchWithAuth(
    `${config.api_url}v3/admin/updateCancelledStatuses`,
    {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    }
  );
  return await req;
};

export const updateHygHold = async (data: UpdateHygHoldPayload, idempotencyKey: string): Promise<any> => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/updateHygHold`, {
    method: "POST",
    headers: { "x-idempotency-key": idempotencyKey },
    body: JSON.stringify(data) // Stringify
  });
  return await req;
};

export const updateHygBlocked = async (data: UpdateHygBlockedPayload, idempotencyKey: string): Promise<any> => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/updateHygBlocked`, {
    method: "POST",
    headers: { "x-idempotency-key": idempotencyKey },
    body: JSON.stringify(data) // Stringify
  });
  return await req;
};

export const uploadToS3 = async (file: File, fileType: string): Promise<string> => {
  let response = await fetchWithAuth(
    `${config.api_url}v3/aws/uploadCredentials?mimeType=${
      file.type
    }&fileName=${file.name + "." + fileType}`,
    {
      method: "GET"
    }
  );
  const { url, signedRequest }: S3UploadCredentials = await response.json();
  await fetch(signedRequest, {
    method: "PUT",
    headers: {
      "Content-Type": `${file.type}` // Explicit Content-Type, no auth needed here
    },
    body: file
  });
  return url;
};

// Upload office document to S3 via dashboard endpoint (stores in /dashboard/ folder)
export const uploadOfficeDocumentToS3 = async (file: File) => {
  console.log("🔍 Uploading office document:", file.name, file.type);

  const response = await fetchWithAuth(
    `${
      config.api_url
    }v3/aws/uploadCredentialsForDashboard?mimeType=${encodeURIComponent(
      file.type
    )}&fileName=${encodeURIComponent(file.name)}`,
    {
      method: "GET"
    }
  );

  const { url, signedRequest } = await response.json();
  console.log("🔍 Got signed URL, uploading to S3...");

  // Upload directly to S3 using signed URL
  await fetch(signedRequest, {
    method: "PUT",
    headers: {
      "Content-Type": file.type
    },
    body: file
  });

  console.log("🔍 Upload complete! Document URL:", url);
  return url;
};

export interface UploadToApiOptions {
  /** Hygienist.type values to target. Talent target only. Allowed: 0, 1. Empty/omitted = all allowed roles. */
  roles?: number[];
  /** 2-letter province/state codes. Empty/omitted = all regions. */
  provinces?: string[];
  /** Required expiry date (YYYY-MM-DD) — T2-6084 */
  expiresAt: string;
  /** T2-6085: id-list targeting type. 'hygienist' (Talent) | 'location' (Office). */
  targetIdListType?: "hygienist" | "location" | null;
  /** T2-6085: validated ids to target. Empty/omitted = no id-list targeting. */
  targetIds?: number[];
}

/** T2-6085: a row the parse endpoint could not accept. */
export interface PhotoAnnounceInvalidRow {
  row: number;
  raw: string;
  reason: string;
}

/** T2-6085: result of validating an uploaded id list. */
export interface PhotoAnnounceParseResult {
  idType: "hygienist" | "location";
  validIds: number[];
  invalidRows: PhotoAnnounceInvalidRow[];
  totalRows: number;
  validCount: number;
  invalidCount: number;
}

export const uploadToApi = async (
  url: string,
  announceUrl: string | undefined | null = null,
  target: string = "talent",
  options: UploadToApiOptions
) => {
  // The uploading admin id is derived server-side from the authenticated JWT
  // (req.user.id) and written to PhotoAnnounce.uploadedByAdmin. We deliberately
  // do NOT send an adminId field from the client — a previous client-supplied
  // adminId was spoofable in the audit trail (PR #2091 review).
  const body: Record<string, unknown> = {
    url,
    announceUrl,
    target,
    expiresAt: options.expiresAt
  };
  if (options.roles && options.roles.length > 0) {
    body.roles = options.roles;
  }
  if (options.provinces && options.provinces.length > 0) {
    body.provinces = options.provinces;
  }
  // T2-6085: optional id-list targeting.
  if (
    options.targetIdListType &&
    options.targetIds &&
    options.targetIds.length > 0
  ) {
    body.targetIdListType = options.targetIdListType;
    body.targetIds = options.targetIds;
  }

  const authHeaders = await ensureAdminApiJwtHeaders();
  const response = await fetch(`${config.api_url}v3/admin/uploadImage`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders
    },
    body: JSON.stringify(body)
  });
  let data: Record<string, unknown> = {};
  try {
    data = await response.json();
  } catch {
    // 401 from JWT middleware may return non-JSON body
  }
  if (!response.ok) {
    const errObj = data && data.error;
    const nestedMessage =
      typeof errObj === "object" &&
      errObj !== null &&
      "message" in errObj &&
      typeof (errObj as { message: unknown }).message === "string"
        ? (errObj as { message: string }).message
        : null;
    const message =
      (typeof data.message === "string" && data.message) ||
      nestedMessage ||
      (response.status === 401
        ? "Admin not authenticated — log out and sign in with username + password."
        : `Photo announce save failed (HTTP ${response.status})`);
    throw new Error(message);
  }
  return data;
};

export const masterPassCheck = async (masterPass: string) => {
  let response = await fetchWithAuth(`${config.api_url}v3/admin/masterpass`, {
    method: "POST",
    body: JSON.stringify({ masterPass }) // Stringify
  });
  return await response.json();
};

// T2-6183: Photo Announce upload history (paginated, newest first).
// Uses the same Admin API JWT path as uploadImage so Cognito-only sessions
// send the correct token type to /v3/admin/* (PR #417 review).
export const getPhotoAnnounceHistory = async (params: {
  page?: number;
  pageSize?: number;
  target?: string;
  status?: string;
  from?: string;
  to?: string;
}) => {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.target) search.set("target", params.target);
  if (params.status) search.set("status", params.status);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  const authHeaders = await ensureAdminApiJwtHeaders();
  const response = await fetch(
    `${config.api_url}v3/admin/photoAnnounce/history?${search}`,
    { headers: authHeaders }
  );
  return await response.json();
};

// T2-6184: which targets currently have a live image.
export const getPhotoAnnounceActiveStatus = async () => {
  const authHeaders = await ensureAdminApiJwtHeaders();
  const response = await fetch(
    `${config.api_url}v3/admin/photoAnnounce/active`,
    { headers: authHeaders }
  );
  return await response.json();
};

// T2-6184: take down the live image for a target (re-validates master pass server-side).
export const removePhotoAnnounce = async (target: string, masterPass: string) => {
  const authHeaders = await ensureAdminApiJwtHeaders();
  const response = await fetch(
    `${config.api_url}v3/admin/photoAnnounce/remove`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders
      },
      body: JSON.stringify({ target, masterPass })
    }
  );
  if (!response.ok) {
    let message = "Could not remove the live image.";
    try {
      const data = await response.json();
      message = data.error || data.message || message;
    } catch (e) {
      /* non-JSON */
    }
    throw new Error(message);
  }
  return await response.json();
};

// T2-6085: validate an uploaded id list (parsed client-side from .xlsx/.csv)
// and return the 'N valid / M invalid' preview. AP role 1 only.
export const parsePhotoAnnounceTargetIds = async (
  idType: "hygienist" | "location",
  rows: Array<string | number>
): Promise<PhotoAnnounceParseResult> => {
  const authHeaders = await ensureAdminApiJwtHeaders();
  const response = await fetch(
    `${config.api_url}v3/admin/photoAnnounce/parseTargetIdFile`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders
      },
      body: JSON.stringify({ idType, rows })
    }
  );
  let data: Record<string, unknown> = {};
  try {
    data = await response.json();
  } catch {
    /* non-JSON (e.g. 401) */
  }
  if (!response.ok) {
    const code = typeof data.error === "string" ? data.error : null;
    if (code === "TOO_MANY_IDS") {
      throw new Error("Too many IDs. The maximum is 10,000 valid IDs.");
    }
    if (code === "TOO_MANY_ROWS") {
      throw new Error("This file has too many rows. Please reduce it and try again.");
    }
    throw new Error(
      (typeof data.message === "string" && data.message) ||
        code ||
        `Could not validate the ID list (HTTP ${response.status})`
    );
  }
  return data as unknown as PhotoAnnounceParseResult;
};

// ---------------------------------------------------------------------------
// T2-6151: Message Users (in-app Bell broadcast) — AP role 1 only
// ---------------------------------------------------------------------------

export type MessageUsersIdListType = "hygienist" | "dentist";

export interface MessageUsersAudience {
  roles: number[];
  provinces?: string[];
  countries?: number[];
  idList?: { type: MessageUsersIdListType; ids: number[] } | null;
}

export interface MessageUsersParseResult {
  idType: MessageUsersIdListType;
  validIds: number[];
  invalidRows: Array<{ row: number; raw: string; reason: string }>;
  totalRows: number;
  validCount: number;
  invalidCount: number;
}

export interface MessageUsersBroadcastRow {
  id: number;
  createdAt: string;
  createdByUserId: number;
  teamMemberName: string;
  title: string;
  message: string;
  audienceFilters: MessageUsersAudience | null;
  audienceSummary: string | null;
  recipientCount: number;
  status: string;
}

const messageUsersAuthFetch = async (
  path: string,
  options: RequestInit = {}
): Promise<Response> => {
  const authHeaders = await ensureAdminApiJwtHeaders();
  return fetch(`${config.api_url}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...(options.headers || {})
    }
  });
};

export const previewMessageUsersCount = async (
  audience: MessageUsersAudience
): Promise<{ recipientCount: number; sampleRecipientUserIds: number[] }> => {
  const response = await messageUsersAuthFetch(
    "v3/admin/notifications/broadcast/preview-count",
    {
      method: "POST",
      body: JSON.stringify({ audience })
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (data && (data.message || data.error)) ||
        `Preview failed (HTTP ${response.status})`
    );
  }
  return data;
};

export const sendMessageUsersBroadcast = async (payload: {
  teamMemberName: string;
  title: string;
  message: string;
  audience: MessageUsersAudience;
  confirm?: boolean;
}): Promise<{
  broadcastId: number;
  recipientCount: number;
  sampleRecipientUserIds: number[];
  status: string;
}> => {
  const response = await messageUsersAuthFetch(
    "v3/admin/notifications/broadcast",
    {
      method: "POST",
      body: JSON.stringify(payload)
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err: any = new Error(
      (data && (data.message || data.error)) ||
        `Send failed (HTTP ${response.status})`
    );
    err.code = data && data.error;
    err.details = data && data.details;
    throw err;
  }
  return data;
};

export const parseMessageUsersIdFile = async (
  idType: MessageUsersIdListType,
  rows: Array<string | number>
): Promise<MessageUsersParseResult> => {
  const response = await messageUsersAuthFetch(
    "v3/admin/notifications/broadcast/parse-file",
    {
      method: "POST",
      body: JSON.stringify({ idType, rows })
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (data && (data.message || data.error)) ||
        `Could not validate the ID list (HTTP ${response.status})`
    );
  }
  return data as MessageUsersParseResult;
};

export const listMessageUsersBroadcasts = async (
  limit = 50,
  offset = 0
): Promise<{ data: MessageUsersBroadcastRow[] }> => {
  const response = await messageUsersAuthFetch(
    `v3/admin/notifications/broadcast?limit=${limit}&offset=${offset}`
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (data && (data.message || data.error)) ||
        `Could not load history (HTTP ${response.status})`
    );
  }
  return data;
};

export const getHireJobs = async () => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/hireJobs`);
  return await req.json();
};

// T2-5639: Featured Listings transparency table for Support.
export const getFeaturedListings = async () => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/featuredListings`);
  return await req.json();
};

// T2-5640: Boosted Posts transparency table for Support.
export const getBoostedPosts = async () => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/boostedPosts`);
  return await req.json();
};

// T2-6449: Fetch the admin-only FFS Support Quick Reference PDF as a Blob.
// The endpoint is gated by the /admin JWT guard, which requires the Admin API
// JWT (isAdmin + id). We use ensureAdminApiJwtHeaders() — which exchanges a
// Cognito-only session into the Admin API JWT when needed — rather than generic
// fetchWithAuth(), which can fall back to a Cognito id token the /admin guard
// rejects (401). Mirrors the photo-announce admin calls. The caller opens the
// resulting object URL in a new tab.
export const fetchFfsReferenceSheet = async (): Promise<Blob> => {
  const authHeaders = await ensureAdminApiJwtHeaders();
  const response = await fetch(`${config.api_url}v3/admin/ffsReferenceSheet`, {
    headers: authHeaders
  });
  if (!response.ok) {
    throw new Error(`FFS Reference Sheet request failed: ${response.status}`);
  }
  return await response.blob();
};

// T2-6236: live Available Now list (repurposed Reliability List page).
export const getAvailableNowTalent = async () => {
  const req = await fetchWithAuth(`${config.api_url}v3/admin/availableNow`, {
    method: "GET"
  });
  if (!req.ok) {
    throw new Error("Failed to load Available Now list");
  }
  return await req.json();
};

export const setFreePeriod = async (FreePeriods: any) => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/setFreePeriod`, {
    method: "PUT",
    body: JSON.stringify(FreePeriods) // Stringify
  });
  return await req.json();
};

export const updateHireJob = async (data: updateHireJobInterface) => {
  let req = await fetchWithAuth(`${config.api_url}v3/hireJobs/${data.id}`, {
    method: "PUT",
    body: JSON.stringify(data) // Stringify
  });
  return await req.json();
};

export const approveHireJob = async (data: any) => {
  await fetchWithAuth(
    // No return needed?
    `${config.api_url}v3/hireJobs/analytics/approvePostedHireJob/${data.id}`,
    {
      method: "PUT",
      body: JSON.stringify(data) // Stringify
    }
  );
};

export const getHistory = async () => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/adminHistory`, {
    method: "GET"
  });
  return await req.json();
};

export const getNotes = async () => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/notes`, {
    method: "GET"
  });
  return await req.json();
};

// T2-6229: manual admin override for a Talent's About Me.
export const updateHygienistAbout = async (
  hygienistId: number,
  about: string
) => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/hygienist/${hygienistId}/about`,
    {
      method: "POST",
      body: JSON.stringify({ about })
    }
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      body.message || body.error || `Failed to update About (${response.status})`
    );
  }
  return body;
};

// T2-6546: view/set a Canadian Dental Assistant's Admin/Reception shift
// preference (Hygienist.agreedAdmin). preference: 'optIn' | 'optOut' | 'reset'.
export const updateHygienistAdminPreference = async (
  hygienistId: number,
  preference: "optIn" | "optOut" | "reset"
) => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/hygienist/${hygienistId}/admin-preference`,
    {
      method: "POST",
      body: JSON.stringify({ preference })
    }
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      body.message ||
        body.error ||
        `Failed to update Admin/Reception preference (${response.status})`
    );
  }
  return body as {
    success: boolean;
    hygienistId: number;
    preference: "optIn" | "optOut" | "reset";
    agreedAdmin: string | null;
    editedBy: string | null;
  };
};

// T2-6513: DA Level / Designation options + update (province-aware).
export const getHygienistQualificationOptions = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/hygienist/${hygienistId}/qualification-options`
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      body.message ||
        body.error ||
        `Failed to load qualification options (${response.status})`
    );
  }
  return body as {
    hygienistId: number;
    province: string | null;
    currentSkillId: number | null;
    currentSkillName: string | null;
    // T2-7535: every saved designation; more than one (e.g. CDA and DA) is a conflict.
    currentSkillIds?: number[];
    currentSkillNames?: string[];
    hasConflict?: boolean;
    options: Array<{ id: number; name: string; label: string }>;
  };
};

export const updateHygienistQualificationLevel = async (
  hygienistId: number,
  skillId: number
) => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/hygienist/${hygienistId}/qualification-level`,
    {
      method: "PUT",
      body: JSON.stringify({ skillId })
    }
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      body.message ||
        body.error ||
        `Failed to update DA Level (${response.status})`
    );
  }
  return body as {
    success: boolean;
    hygienistId: number;
    previousSkillId: number | null;
    previousSkillName: string | null;
    newSkillId: number;
    newSkillName: string;
    newSkillLabel: string;
    legacyLevel: number;
    preservedSkillIds: number[];
    editedBy: string | null;
  };
};

// T2-6232: Blocks & Favorites sections on Talent / Location profiles.
export const getHygienistBlocksFavourites = async (hygienistId: number) => {
  const req = await fetchWithAuth(
    `${config.api_url}v3/admin/hygienist/${hygienistId}/blocks-favourites`
  );
  if (!req.ok) {
    throw new Error("Failed to load blocks & favourites");
  }
  return await req.json();
};

export const getLocationBlocksFavourites = async (locationId: number) => {
  const req = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/blocks-favourites`
  );
  if (!req.ok) {
    throw new Error("Failed to load blocks & favourites");
  }
  return await req.json();
};

export const addBlocked = async (data: any, tablename: string) => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/blocked`, {
    method: "PUT",
    body: JSON.stringify({ data, tablename: tablename }) // Stringify
  });
  return await req.json();
};

export const removeBlocked = async (tablename: string, id: number | string) => {
  let req = await fetchWithAuth(
    `${config.api_url}v3/admin/blocked/${tablename}/${id}`,
    {
      method: "PUT"
    }
  );
  return await req.json();
};

export const addFavourite = async (data: any, tablename: string) => {
  try {
    let req = await fetchWithAuth(`${config.api_url}v3/admin/favourite`, {
      method: "PUT",
      body: JSON.stringify({ data, tablename: tablename }) // Stringify
    });
    
    if (!req.ok) {
      const errorText = await req.text().catch(() => req.statusText);
      console.error(`addFavourite: API error for ${tablename}:`, {
        status: req.status,
        statusText: req.statusText,
        error: errorText
      });
      throw new Error(`Failed to add favourite: ${req.status} ${req.statusText}`);
    }
    
    return await req.json();
  } catch (error) {
    console.error(`addFavourite: Error for ${tablename}:`, error);
    throw error;
  }
};

export const removeFavourite = async (tablename: string, id: number | string) => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/favourite/${tablename}/${id}`,
      {
        method: "PUT"
      }
    );
    
    if (!req.ok) {
      const errorText = await req.text().catch(() => req.statusText);
      console.error(`removeFavourite: API error for ${tablename}/${id}:`, {
        status: req.status,
        statusText: req.statusText,
        error: errorText
      });
      throw new Error(`Failed to remove favourite: ${req.status} ${req.statusText}`);
    }
    
    return await req.json();
  } catch (error) {
    console.error(`removeFavourite: Error for ${tablename}/${id}:`, error);
    throw error;
  }
};

export const filterDays = async (days: number, table: string) => {
  const response = await fetchWithAuth(`${config.api_url}v3/admin/${table}`, {
    method: "POST",
    body: JSON.stringify({ days }) // Stringify
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(
      `Failed to fetch ${table}: ${response.status} ${response.statusText} - ${errorText}`
    );
  }
  return await response.json();
};

export const getRates = async () => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/Rates`, {
    method: "GET"
  });
  return await req.json();
};

export const getDataForDispute = async (data: any) => {
  try {
    // First, validate and clean all input data
    const cleanedData = validateDisputeSearchData(data);

    console.log("🔍 getDataForDispute: Original data:", data);
    console.log("🔍 getDataForDispute: Cleaned data:", cleanedData);

    // Additional validation - ensure at least one search criteria is provided
    const hasHygienistCriteria =
      cleanedData.hygienist.firstName ||
      cleanedData.hygienist.lastName ||
      cleanedData.hygienist.email ||
      cleanedData.hygienist.id;

    const hasLocationCriteria =
      cleanedData.location.name ||
      cleanedData.location.address ||
      cleanedData.location.email ||
      cleanedData.location.id;

    const hasJobCriteria = cleanedData.jobId;

    if (!hasHygienistCriteria && !hasLocationCriteria && !hasJobCriteria) {
      throw new Error("Please provide at least one search criterion.");
    }

    let req = await fetchWithAuth(`${config.api_url}v3/admin/disputeData`, {
      method: "POST",
      body: JSON.stringify(cleanedData)
    });

    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));

      console.error("🔍 getDataForDispute: API Error Details:", {
        status: req.status,
        statusText: req.statusText,
        errorData: errorData,
        sentData: cleanedData
      });

      // Try to get a detailed error message with workarounds
      const detailedErrorMessage = handleDatabaseError(cleanedData, errorData);
      if (detailedErrorMessage) {
        throw new Error(detailedErrorMessage);
      }

      // Provide more user-friendly error messages for other errors
      let userMessage = "An error occurred while searching for dispute data.";
      if (req.status === 500) {
        userMessage =
          "Server error occurred. Please try again or contact support.";
      } else if (req.status === 404) {
        userMessage = "No data found matching your search criteria.";
      } else if (req.status === 400) {
        userMessage =
          errorData?.message || "Invalid search parameters provided.";
      }

      throw new Error(userMessage);
    }

    return await req.json();
  } catch (err) {
    console.error(
      "Error in getDataForDispute:",
      err instanceof Error ? err.message : err
    );
    throw err;
  }
};

export const updateDonationAmount = async (data: any) => {
  try {
    let req = await fetchWithAuth(`${config.api_url}v3/admin/donation`, {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    });
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (err) {
    console.error(
      "Error in updateDonationAmount:",
      err instanceof Error ? err.message : err
    );
    throw err;
  }
};

export const moveHygienistCancelled = async (data: any) => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/moveHygienistCancelled`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req;
  } catch (err) {
    console.error(
      "Error in moveHygienistCancelled:",
      err instanceof Error ? err.message : err
    );
    throw err;
  }
};

export const getCommentsForDispute = async (data: any) => {
  try {
    // Clean the data before sending to prevent DATETIME errors
    const cleanedData = cleanDataForDatabase(data);

    console.log("🔍 getCommentsForDispute: Original data:", data);
    console.log("🔍 getCommentsForDispute: Cleaned data:", cleanedData);

    let req = await fetchWithAuth(`${config.api_url}v3/admin/disputeInfo`, {
      method: "POST",
      body: JSON.stringify(cleanedData) // Stringify
    });
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));

      console.error("🔍 getCommentsForDispute: API Error:", {
        status: req.status,
        errorData: errorData,
        sentData: cleanedData
      });

      // Provide user-friendly error messages
      let userMessage =
        "An error occurred while retrieving dispute information.";
      if (
        errorData?.error?.message?.includes("DATETIME") ||
        errorData?.message?.includes("DATETIME") ||
        errorData?.error?.message?.includes("ER_WRONG_VALUE")
      ) {
        userMessage =
          "Invalid date format detected. Please check your search criteria and try again.";
      }

      throw new Error(userMessage);
    }
    return await req.json();
  } catch (err) {
    console.error(
      "Error in getCommentsForDispute:",
      err instanceof Error ? err.message : err
    );
    throw err;
  }
};

export const updateShortNotice = async (data: { id: number; change: any }) => {
  try {
    let req = await fetchWithAuth(`${config.api_url}v3/admin/cancellations`, {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    });
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (err) {
    console.error(
      "Error in updateShortNotice:",
      err instanceof Error ? err.message : err
    );
    throw err;
  }
};

export const updateDentistCancelled = async (
  data: dentistCancelledIfDoneInterface
) => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/update`, {
    method: "POST",
    body: JSON.stringify(data) // Stringify
  });
  if (!req.ok) {
    throw new Error(`HTTP error! status: ${req.status}`);
  }
  return await req;
};

export const getShortNoticeCancels = async (id?: number) => {
  const condition = id ? `/${id}` : "";
  let req = await fetchWithAuth(
    `${config.api_url}v3/admin/cancellations${condition}`,
    { method: "GET" }
  );
  if (!req.ok) {
    const errorData = await req
      .json()
      .catch(() => ({ message: req.statusText }));
    throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
  }
  return await req.json();
};

export const getHygienistCancelled = async () => {
  let req = await fetchWithAuth(
    `${config.api_url}v3/admin/getHygienistCancelled`,
    { method: "GET" }
  );
  if (!req.ok) {
    const errorData = await req
      .json()
      .catch(() => ({ message: req.statusText }));
    throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
  }
  return await req.json();
};

export const updateHygienistCancelled = async (
  data: updateHygienistCancelledInterface
) => {
  let req = await fetchWithAuth(
    `${config.api_url}v3/admin/updateHygienistCancelled`,
    {
      method: "PUT",
      body: JSON.stringify(data) // Stringify
    }
  );
  if (!req.ok) {
    throw new Error(`HTTP error! status: ${req.status}`);
  }
  return await req;
};

export const moveTwoPF = async (data: moveTwoPFInterface) => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/giveTwoPF`, {
    method: "POST",
    body: JSON.stringify(data) // Stringify
  });
  if (!req.ok) {
    const errorData = await req
      .json()
      .catch(() => ({ message: req.statusText }));
    throw new Error(errorData.message || errorData.error?.message || `HTTP error! status: ${req.status}`);
  }
  // Return the response object (204 No Content doesn't have a body)
  return req;
};

export const checkRowByTableNameAndID = async (data: {
  id: number;
  tableName: string;
}) => {
  let req = await fetchWithAuth(
    `${config.api_url}v3/admin/checkRowByTableNameAndID`,
    {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    }
  );
  if (!req.ok) {
    const errorData = await req
      .json()
      .catch(() => ({ message: req.statusText }));
    throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
  }
  return await req.json();
};

export const updateResumeURL = async (data: updateResumeURLInterface) => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/updateResumeURL`, {
    method: "PUT",
    body: JSON.stringify(data) // Stringify
  });
  if (!req.ok) {
    throw new Error(`HTTP error! status: ${req.status}`);
  }
  return await req;
};

export const getPaymentIntegrities = async (
  data: getPaymentIntegritiesInterface
) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/paymentIntegrities`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getPaymentIntegrities:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getUnresolvedPayments = async () => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/unresolvedPayments`,
      {
        method: "GET"
      }
    );
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in getUnresolvedPayments:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const sendOfficeFollowUp = async (data: sendOfficeFollowUpInterface) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/paymentIntegrities/sendOfficeFollowUp`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in sendOfficeFollowUp:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const nudgeJames = async (id: number) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/paymentIntegrities/nudgeJames/${id}`,
      {
        method: "POST"
        // No body needed
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in nudgeJames:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const nudgeSent = async (id: number) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/paymentIntegrities/nudgeSent/${id}`,
      {
        method: "POST"
        // No body needed
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in nudgeSent:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const sendHygAsstResponse = async (
  data: sendHygAsstResponseInterface
) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/paymentIntegrities/sendHygAsstResponse`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in sendHygAsstResponse:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const updateInvoiceNotes = async (data: {
  invoiceID: number;
  note: string;
}) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/invoice/updateNotes`,
      {
        method: "PUT",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in updateInvoiceNotes:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getProgressPayments = async (invoiceID: number) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/invoices/${invoiceID}/dashboard`,
      { method: "GET" }
    );
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getProgressPayments:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const updateInvoice = async (data: updareInvoiceInterface) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/invoices/${data.invoiceId}`,
      {
        method: "PUT",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in updateInvoice:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const addAdminLogging = async (data: addLoggingInterface) => {
  try {
    let res = await fetchWithAuth(`${config.api_url}v3/admin/adminLogging`, {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in addAdminLogging:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getAdminLogging = async () => {
  try {
    let res = await fetchWithAuth(`${config.api_url}v3/admin/adminLogging`, {
      method: "GET"
    });
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getAdminLogging:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const searchAdminLogging = async (
  data: Array<searchAdminLoggingInterface | string>
) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/searchAdminLogging`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in searchAdminLogging:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const sendPayNow = async (id: number) => {
  try {
    let res = await fetchWithAuth(`${config.api_url}v3/invoices/${id}/pay`, {
      method: "POST"
      // No body needed
    });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in sendPayNow:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const claimPayment = async (id: number): Promise<ClaimPaymentResult> => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/invoices/${id}/claimPayment`,
      {
        method: "POST",
        headers: { "X-TempStars-Admin-Payment-Contract": "lifecycle-v1" }
        // No body needed
      }
    );
    if (!res.ok) {
      let errorMessage = `HTTP error! status: ${res.status}`;
      let paymentOutcome: string | undefined;
      try {
        const errorData = await res.json();
        paymentOutcome = errorData.data && errorData.data.paymentOutcome;
        if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // If JSON parsing fails, use the default error message
      }
      const error = new Error(errorMessage);
      (error as any).status = res.status;
      (error as any).paymentOutcome = paymentOutcome;
      (error as any).response = res;
      throw error;
    }
    const paymentProjection = await res.json();
    return {
      ...paymentProjection,
      httpStatus: res.status
    } as ClaimPaymentResult;
  } catch (error) {
    console.error(
      "Error in claimPayment:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const confirmManualPayment = async (
  id: number,
  reference: string
): Promise<ClaimPaymentResult> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/invoices/${id}/confirmManualPayment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reference })
    }
  );
  if (!res.ok) {
    let errorMessage = `HTTP error! status: ${res.status}`;
    try {
      const errorData = await res.json();
      errorMessage =
        errorData.error?.message || errorData.message || errorMessage;
    } catch (_) {
      // Preserve the HTTP status fallback when the API response is not JSON.
    }
    throw new Error(errorMessage);
  }
  return {
    ...(await res.json()),
    httpStatus: res.status
  } as ClaimPaymentResult;
};

// 5.27 FB-5: retry a repaired automatic Talent payout. The API re-checks the
// full eligibility predicate under a row lock; the dashboard only offers this
// when the Payment Integrity row reports automaticPayoutRetryEligible === true.
// Never retried automatically: a lost response cannot prove nothing moved.
export const retryAutomaticPayout = async (
  id: number
): Promise<AutomaticPayoutRetryResult> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/invoices/${id}/retryPayment`,
    {
      method: "POST",
      headers: { "X-TempStars-Admin-Payment-Contract": "lifecycle-v1" }
    }
  );
  if (!res.ok) {
    let errorMessage = `HTTP error! status: ${res.status}`;
    try {
      const errorData = await res.json();
      errorMessage =
        errorData.error?.message || errorData.message || errorMessage;
    } catch (_) {
      // Preserve the HTTP status fallback when the API response is not JSON.
    }
    const error = new Error(errorMessage);
    (error as any).status = res.status;
    throw error;
  }
  return {
    ...(await res.json()),
    httpStatus: res.status
  } as AutomaticPayoutRetryResult;
};

export const stripeExpressPayment = async (invoiceID: number) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/stripeExpressPayment`,
      {
        method: "POST",
        headers: { "X-TempStars-Admin-Payment-Contract": "lifecycle-v1" },
        body: JSON.stringify({ invoiceID }) // Stringify
      }
    );
    if (!res.ok) {
      let errorMessage = `HTTP error! status: ${res.status}`;
      try {
        const errorData = await res.json();
        if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // If JSON parsing fails, use the default error message
      }
      const error = new Error(errorMessage);
      (error as any).status = res.status;
      (error as any).response = res;
      throw error;
    }
    return res;
  } catch (error) {
    console.error(
      "Error in stripeExpressPayment:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const addAdminNotes = async (data: {
  entityId: number;
  type: string;
  note: string;
  source: string;
}) => {
  try {
    let res = await fetchWithAuth(`${config.api_url}v3/admin/addAdminNotes`, {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in addAdminNotes:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const shiftsCancelledBilling = async (data: {
  id: number;
  notes: string;
  skipPayment?: boolean;
}) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/shiftsCancelledBilling`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    // The billing view needs the structured conflict response, not an HTTP-only error.
    return res;
  } catch (error) {
    console.error(
      "Error in shiftsCancelledBilling:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const clearShiftsCancelledBilling = async (
  data: shiftsCancelledBillingInterface
) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/clearShiftsCancelledBilling`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in clearShiftsCancelledBilling:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const setFieldToNULL = async (data: {
  tablename: string;
  columnname: string;
  id: number;
}) => {
  try {
    let res = await fetchWithAuth(`${config.api_url}v3/admin/setFieldToNULL`, {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in setFieldToNULL:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const updateHygienistNotes = async (data: {
  id: number;
  note: string;
}) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/updateHygienistNotes`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in updateHygienistNotes:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const setHygienistNotes = async (data: {
  id: number;
  notes: string;
}) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/hygienist/${data.id}`,
      {
        method: "PUT",
        body: JSON.stringify({ Notes: data.notes })
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in setHygienistNotes:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

// T2-5716: Current admin role (used by UI to gate Role 1 features)
export interface CurrentAdminRole {
  adminId: number;
  role: number | null;
  isDeveloper: boolean;
}

export const getCurrentAdminRole = async (): Promise<CurrentAdminRole> => {
  const res = await fetchWithAuth(`${config.api_url}v3/admin/me/role`);
  if (!res.ok) {
    throw new Error(`HTTP error! status: ${res.status}`);
  }
  return res.json();
};

export interface RegionalFreePeriodRevision {
  id: number;
  province: string;
  campaignName: string;
  startDate: string;
  endDate: string;
  enabled: boolean;
  placementFeeWaived: boolean;
  tokensWaived: boolean;
  effectiveFromDate: string;
  inactiveFromDate: string | null;
  supersedesCampaignId: number | null;
  createdByAdmin?: number;
  createdAt?: string;
  updatedAt?: string;
  status: "scheduled" | "active" | "expired" | "inactive";
}

export interface RegionalFreePeriodCampaign extends RegionalFreePeriodRevision {
  rootCampaignId: number;
  latestRevisionId: number;
  editableRevisionId: number | null;
  revisionCount: number;
  currentRevision: RegionalFreePeriodRevision | null;
  pendingRevision: RegionalFreePeriodRevision | null;
  latestRevision: RegionalFreePeriodRevision;
  revisions: RegionalFreePeriodRevision[];
}

export interface RegionalFreePeriodUpdateResult
  extends RegionalFreePeriodRevision {
  revisionCreated: boolean;
  idempotentReplay: boolean;
}

export interface RegionalFreePeriodRequestError extends Error {
  status?: number;
  domainCode?: string;
}

export interface RegionalFreePeriodCampaignInput {
  province: string;
  campaignName: string;
  startDate: string;
  endDate: string;
  placementFeeWaived: boolean;
  tokensWaived: boolean;
}

const regionalFreePeriodRequest = async (
  path: string,
  options: RequestInit = {}
): Promise<any> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/free-periods${path}`,
    options
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    const error = new Error(
      body.error || body.message || `HTTP ${res.status}`
    ) as RegionalFreePeriodRequestError;
    error.status = res.status;
    error.domainCode = body.data && body.data.domainCode;
    throw error;
  }
  return res.json();
};

export const getRegionalFreePeriodCampaigns = async (): Promise<RegionalFreePeriodCampaign[]> => {
  const body = await regionalFreePeriodRequest("");
  return body.campaigns || [];
};

export const createRegionalFreePeriodCampaign = async (
  payload: RegionalFreePeriodCampaignInput
): Promise<RegionalFreePeriodRevision> => {
  const body = await regionalFreePeriodRequest("", {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return body.campaign;
};

export const updateRegionalFreePeriodCampaign = async (
  campaignId: number,
  payload: RegionalFreePeriodCampaignInput
): Promise<RegionalFreePeriodUpdateResult> => {
  const body = await regionalFreePeriodRequest(`/${campaignId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
  return body.campaign;
};

export const deactivateRegionalFreePeriodCampaign = async (
  campaignId: number
): Promise<RegionalFreePeriodRevision> => {
  const body = await regionalFreePeriodRequest(`/${campaignId}/deactivate`, {
    method: "POST"
  });
  return body.campaign;
};

// T2-6869: Office Promotions (new-office free placement)
export interface OfficePromotionAdminRow {
  id: number;
  state_code: string;
  promotion_start_date: string;
  promotion_cutoff_date: string;
  promotion_duration_days: number;
  is_active: boolean;
  status: "Scheduled" | "Active" | "Expired" | "Off";
  officesCurrentlyInPromo: number;
  created_at: string;
  updated_at: string;
}

export interface OfficePromotionAdminInput {
  state_code: string;
  promotion_start_date: string;
  promotion_cutoff_date: string;
  promotion_duration_days?: number;
  is_active?: boolean;
}

const officePromotionRequest = async (
  path: string,
  options: RequestInit = {}
): Promise<any> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/office-promotions${path}`,
    options
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error(body.error || body.message || `HTTP ${res.status}`);
  }
  return res.json();
};

export const getOfficePromotions = async (): Promise<OfficePromotionAdminRow[]> => {
  const body = await officePromotionRequest("");
  return body.promotions || [];
};

export const createOfficePromotion = async (
  payload: OfficePromotionAdminInput
): Promise<OfficePromotionAdminRow> => {
  const body = await officePromotionRequest("", {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return body.promotion;
};

export const updateOfficePromotion = async (
  id: number,
  payload: Partial<OfficePromotionAdminInput>
): Promise<OfficePromotionAdminRow> => {
  const body = await officePromotionRequest(`/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
  return body.promotion;
};

export const deactivateOfficePromotion = async (
  id: number
): Promise<OfficePromotionAdminRow> => {
  const body = await officePromotionRequest(`/${id}/deactivate`, {
    method: "POST"
  });
  return body.promotion;
};

export const reactivateOfficePromotion = async (
  id: number
): Promise<OfficePromotionAdminRow> => {
  const body = await officePromotionRequest(`/${id}/reactivate`, {
    method: "POST"
  });
  return body.promotion;
};

// T2-6984: SNC Merch Recipients fulfillment queue — AP role 1 only
export interface SncMerchRecipientRow {
  id: number;
  hygienistId: number;
  talentName: string;
  talentEmail: string | null;
  talentTier: string;
  talentStatus: number | null;
  completedCount: number;
  milestone: number;
  milestoneLabel: string;
  mailingAddress: string;
  earnedOn: string;
  doneOn: string | null;
  doneByAdminId: number | null;
  doneByAdmin: string | null;
}

const sncMerchRecipientRequest = async (
  path: string,
  options: RequestInit = {}
): Promise<any> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/snc-merch-recipients${path}`,
    options
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error(body.error || body.message || `HTTP ${res.status}`);
  }
  return res.json();
};

export const getSncMerchRecipients = async (
  tab: "active" | "history" = "active"
): Promise<SncMerchRecipientRow[]> => {
  const body = await sncMerchRecipientRequest(`?tab=${tab}`);
  return body.recipients || [];
};

export const markSncMerchRecipientDone = async (
  id: number
): Promise<{ ok: boolean }> => {
  return sncMerchRecipientRequest(`/${id}/done`, { method: "POST" });
};

export const undoSncMerchRecipientDone = async (
  id: number
): Promise<{ ok: boolean }> => {
  return sncMerchRecipientRequest(`/${id}/undo`, { method: "POST" });
};

// T2-5716: Bulk placement fee upload (Role 1 only)
export interface BulkPlacementFeeRow {
  dentistId: number;
  email: string;
  numCredits: number;
  reason?: string;
}

export interface BulkPlacementFeeRowResult {
  row: number;
  status: "success" | "error";
  error?: string;
  dentistId?: number;
  email?: string;
  numCredits?: number;
  newTotal?: number;
  locationId?: number;
  practiceName?: string;
  reason?: string;
  input?: unknown;
}

export interface BulkPlacementFeeResponse {
  batchId: number;
  note: string;
  uploadedByAdminId: number;
  uploaderUsername: string | null;
  rowCount: number;
  successCount: number;
  failureCount: number;
  results: BulkPlacementFeeRowResult[];
}

export const bulkUploadPlacementFees = async (
  payload: { note: string; rows: BulkPlacementFeeRow[] }
): Promise<BulkPlacementFeeResponse> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/placementFees/bulkUpload`,
    {
      method: "POST",
      body: JSON.stringify(payload)
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error(body && (body.error || body.message) ? body.error || body.message : `HTTP ${res.status}`);
  }
  return res.json();
};

export const updatePlacementFees = async (data: updatePF) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/updatePlacementFees`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        body.message || "Could not confirm the credit update. Refresh the office before trying again."
      );
    }
    return res;
  } catch (error) {
    console.error(
      "Error in updatePlacementFees:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getCancelledShiftsToBillByJobId = async (id: string) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/SNCBillingBoard/${id}`,
      {
        method: "POST" // Still seems like this should be GET? Assuming POST is correct.
        // No body if it's just an ID in the URL
      }
    );
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getCancelledShiftsToBillByJobId:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const updateCourseDisputeAnalyzer = async (
  data: courseDisputeAnalyzer
) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/courseDisputeAnalyzer`,
      {
        method: "POST",
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in updateCourseDisputeAnalyzer:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getCourseDisputeAnalyzer = async (id: number) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/courseDisputeAnalyzer/${id}`,
      { method: "GET" }
    );
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getCourseDisputeAnalyzer:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const totalItemsByTable = async (data: {
  tableName: string;
  search?: string;
  searchColumns?: string[];
  columnNames?: Array<string>;
}) => {
  try {
    console.log("📊 totalItemsByTable called with:", data);
    let res = await fetchWithAuth(`${config.api_url}v3/admin/totalItems`, {
      method: "POST",
      body: JSON.stringify(data) // Stringify
    });
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    const result = await res.json();
    console.log("📊 totalItemsByTable result:", result);
    const count = result.count !== undefined ? result.count : result;
    return count;
  } catch (error) {
    console.error(
      "Error in totalItemsByTable:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const takeTwoMonthBreak = async (data: takeTwoMonthBreakInterface, idempotencyKey: string) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/takeTwoMonthBreak`,
      {
        method: "POST",
        headers: { "x-idempotency-key": idempotencyKey },
        body: JSON.stringify(data) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in takeTwoMonthBreak:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getPaginateItems = async (data: paginateTable) => {
  let req = await fetchWithAuth(`${config.api_url}v3/admin/getPaginateItems`, {
    method: "POST",
    body: JSON.stringify(data) // Stringify
  });
  if (!req.ok) {
    const errorData = await req
      .json()
      .catch(() => ({ message: req.statusText }));
    throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
  }
  return await req.json();
};

export const getAllPaginateItems = async (
  request: paginateTable,
  options: { maxPages?: number } = {}
) => {
  const limit = Math.max(request.limit || 1, 1);
  const maxPages = options.maxPages ?? 100;
  const aggregated: Array<Record<string, any>> = [];
  const seenKeys = new Set<string>();

  for (let page = request.page || 1; page <= maxPages; page += 1) {
    const pageItems = await getPaginateItems({
      ...request,
      page
    });

    if (!Array.isArray(pageItems) || pageItems.length === 0) {
      break;
    }

    pageItems.forEach((item, index) => {
      const key =
        item && typeof item === "object" && "id" in item
          ? `id:${(item as Record<string, unknown>).id}`
          : `page:${page}-row:${index}`;

      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        aggregated.push(item);
      }
    });

    if (pageItems.length < limit) {
      break;
    }
  }

  return aggregated;
};

export const getHygienistShifts = async (id: number, jobId = 0) => {
  try {
    let req = await fetchWithAuth(`${config.api_url}v3/admin/hygienistShifts`, {
      method: "POST",
      body: JSON.stringify({ id, jobId }) // Stringify
    });
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in getHygienistShifts:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const cancelJobHygienist = async (
  hygienistId: number,
  jobId: number,
  note: string
) => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/cancelJobHygienist`,
      {
        method: "POST",
        body: JSON.stringify({ hygienistId, jobId, note }) // Stringify
      }
    );
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in cancelJobHygienist:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const uncompleteJob = async (jobId: number, note: string = "") => {
  try {
    let req = await fetchWithAuth(`${config.api_url}v3/admin/uncompleteJob`, {
      method: "POST",
      body: JSON.stringify({ jobId, note }) // Stringify
    });
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in uncompleteJob:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const updateHourlyRate = async (jobId: number, hourlyRate: number) => {
  try {
    let res = await fetchWithAuth(
      `${config.api_url}v3/admin/updateHourlyRate`,
      {
        method: "POST",
        body: JSON.stringify({ jobId, hourlyRate }) // Stringify
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return res;
  } catch (error) {
    console.error(
      "Error in updateHourlyRate:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/** T2-6868: update Job.paymentMethod for Posted / Has Offers / Booked jobs. */
export const updateJobPaymentMethod = async (
  jobId: number,
  paymentMethod: number
): Promise<{
  success: boolean;
  jobId: number;
  previousPaymentMethod: number | null;
  paymentMethod: number;
  status: number;
  country: number | null;
  isCentralBooked: number | null;
  paymentMethodLabel: string;
}> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/job/${jobId}/payment-method`,
    {
      method: "PUT",
      body: JSON.stringify({ paymentMethod })
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || `Failed to update payment method (${response.status})`
    );
  }
  return await response.json();
};

export const getCancelledByJobId = async (jobId: number) => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/getHygCancelledByJobId`,
      {
        method: "POST",
        body: JSON.stringify({ jobId }) // Stringify
      }
    );
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in getCancelledByJobId:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const cancelJobDentist = async (jobId: number, note: string = "") => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/cancelJobDentist`,
      {
        method: "POST",
        body: JSON.stringify({ jobId, note }) // Stringify
      }
    );
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in cancelJobDentist:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const cancelJobAgent = async (
  jobId: number,
  note: string = "",
  repostShift: boolean = false
) => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/cancelJobAgent`,
      {
        method: "POST",
        body: JSON.stringify({ jobId, note, repostShift })
      }
    );
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in cancelJobAgent:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getOffersByJobId = async (jobId: number) => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/getOffersByJobId`, {
      method: "POST",
      body: JSON.stringify({ jobId })
    });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getOffersByJobId:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const adminAcceptOffer = async (offerId: number) => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/acceptOffer`, {
      method: "POST",
      body: JSON.stringify({ offerId })
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in adminAcceptOffer:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const adminDeclineOffer = async (offerId: number, reason: string) => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/declineOffer`, {
      method: "POST",
      body: JSON.stringify({ offerId, reason })
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in adminDeclineOffer:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const adminRequestResubmit = async (offerId: number) => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/requestResubmit`, {
      method: "POST",
      body: JSON.stringify({ offerId })
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in adminRequestResubmit:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getChatHistory = async (jobId: number): Promise<ChatHistoryResponse> => {
  try {
    let req = await fetchWithAuth(
      `${config.api_url}v3/admin/chatHistory/${jobId}`,
      {
        method: "GET"
      }
    );
    if (!req.ok) {
      const errorData = await req
        .json()
        .catch(() => ({ message: req.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${req.status}`);
    }
    return await req.json();
  } catch (error) {
    console.error(
      "Error in getChatHistory:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

// ============================================================================
// Location-Filtered Data API Functions (for Dental Offices Master-Detail View)
// ============================================================================

/**
 * Get all jobs for a specific location
 */
export const getJobsByLocation = async (locationId: number) => {
  try {
    const response = await fetchWithAuth(
      `${config.api_url}v3/jobs/${locationId}/locationJobs`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(
      "Error in getJobsByLocation:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Get invoices for jobs at a specific location
 * Note: Fetches all invoices and filters client-side by location
 */
export const getInvoicesByLocation = async (locationId: number) => {
  try {
    const response = await fetchWithAuth(
      `${config.api_url}v3/admin/invoice`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const allInvoices = await response.json();
    // Filter invoices by jobs at this location
    // We'll need to cross-reference with jobs or invoice data should have locationId
    return allInvoices;
  } catch (error) {
    console.error(
      "Error in getInvoicesByLocation:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Get payments for jobs at a specific location
 */
export const getPaymentsByLocation = async (locationId: number) => {
  try {
    const response = await fetchWithAuth(
      `${config.api_url}v3/admin/unresolvedPayments`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const allPayments = await response.json();
    // May need to filter client-side by locationId
    return allPayments;
  } catch (error) {
    console.error(
      "Error in getPaymentsByLocation:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

// Paginated location detail endpoints

export interface PaginatedResponse<T = any> {
  data: T[];
  total: number;
}

export const getLocationSiblings = async (
  locationId: number
): Promise<PaginatedResponse> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/siblings`
  );
  if (!response.ok) {
    throw new Error(`Failed to load associated locations (${response.status})`);
  }
  return await response.json();
};

export interface MandrillRejectEntry {
  email: string;
  reason: string;
  detail?: string;
  last_event_at?: string;
  expires_at?: string;
  expired?: boolean;
  subaccount?: string | null;
  /** T2-6235: true when denylist email maps to a TempStars User (login or Location email). */
  canResubscribe?: boolean;
  tempStarsUserId?: number | null;
}

export const listMandrillRejects = async (
  email?: string,
  includeExpired = false
): Promise<MandrillRejectEntry[]> => {
  const params = new URLSearchParams();
  if (email) {
    params.set("email", email);
  }
  if (includeExpired) {
    params.set("include_expired", "true");
  }
  const qs = params.toString();
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/mandrill/rejects${qs ? `?${qs}` : ""}`
  );
  if (!response.ok) {
    throw new Error(`Failed to load spam list (${response.status})`);
  }
  return await response.json();
};

export const deleteMandrillReject = async (
  email: string,
  subaccount?: string | null
): Promise<MandrillRejectEntry> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/mandrill/rejects/delete`,
    {
      method: "POST",
      body: JSON.stringify({ email, subaccount: subaccount || null })
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to remove from spam list (${response.status})`);
  }
  return await response.json();
};

// T2-6235: combined re-subscribe — Mandrill reject removal + TempStars-side
// suppression clear + MailChimp re-subscribe + talent notification flags.
export interface ResubscribeResult {
  success: boolean;
  message: string;
  userEmail: string;
  userId: number;
  mandrillRejectRemoved: boolean;
  mailChimpSubscribed: boolean;
  hygienistResubscribed: boolean;
}

export const resubscribeUser = async (
  email: string,
  subaccount?: string | null
): Promise<ResubscribeResult> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/resubscribeUser`,
    {
      method: "POST",
      body: JSON.stringify({ email, subaccount: subaccount || null })
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || err.error || `Failed to re-subscribe (${response.status})`
    );
  }
  return await response.json();
};

// T2-6224: unified Posted Jobs list for the Location profile.
export const getLocationPostedJobs = async (
  locationId: number,
  includePast = false
) => {
  const params = includePast ? "?includePast=true" : "";
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/posted-jobs${params}`
  );
  if (!response.ok) {
    throw new Error(`Failed to load posted jobs (${response.status})`);
  }
  const body = await response.json();
  return body.data || [];
};

export const getLocationJobsPaginated = async (
  locationId: number,
  page: number,
  limit: number,
  sortBy: string,
  sortDesc: boolean,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortDesc: String(sortDesc)
  });
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/jobs?${params}`,
    { signal }
  );
  return await response.json();
};

export const getLocationInvoicesPaginated = async (
  locationId: number,
  page: number,
  limit: number,
  sortBy: string,
  sortDesc: boolean,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortDesc: String(sortDesc)
  });
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/invoices?${params}`,
    { signal }
  );
  return await response.json();
};

export const getLocationPaymentsPaginated = async (
  locationId: number,
  page: number,
  limit: number,
  sortBy: string,
  sortDesc: boolean,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortDesc: String(sortDesc)
  });
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/payments?${params}`,
    { signal }
  );
  return await response.json();
};

// T2-6221: Placement Fee history for a location (read-only, newest first).
export const getLocationPlacementFeesPaginated = async (
  locationId: number,
  page: number,
  limit: number,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit)
  });
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/placement-fees?${params}`,
    { signal }
  );
  return await response.json();
};

/** T2-6704: Office cancellations for a Location profile */
export const getLocationCancellationsPaginated = async (
  locationId: number,
  page: number,
  limit: number,
  sortBy: string,
  sortDesc: boolean,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortDesc: String(sortDesc)
  });
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/cancellations?${params}`,
    { signal }
  );
  return await response.json();
};

export const updateLocationCancellation = async (
  locationId: number,
  cancellationId: number,
  payload: { reason: string | null; details: string }
) => {
  const response = await fetchWithAuth(
    `v3/admin/location/${locationId}/cancellations/${cancellationId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload)
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText;
    try {
      const parsed = JSON.parse(errorText);
      errorMessage = parsed?.error || parsed?.message || errorText;
    } catch {
      errorMessage = errorText;
    }
    throw new Error(errorMessage || `Failed to update cancellation: ${response.status}`);
  }
  return await response.json();
};

// T2-6223: update a Location's login email (writes through to User.email).
// Throws an Error carrying the API message on validation / uniqueness failure.
export const updateLocationLoginEmail = async (
  locationId: number,
  email: string
): Promise<{ locationId: number; dentistId: number; email: string }> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/login-email`,
    {
      method: "PUT",
      body: JSON.stringify({ email })
    }
  );
  if (!response.ok) {
    let message = "Could not update the login email.";
    try {
      const data = await response.json();
      message = data.message || data.error || message;
    } catch (e) {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return await response.json();
};

export const getLocationTalentReviewsPaginated = async (
  locationId: number,
  page: number,
  limit: number,
  sortBy: string,
  sortDesc: boolean,
  search: string,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortDesc: String(sortDesc)
  });
  if (search && search.trim()) {
    params.set('search', search.trim());
  }
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/location/${locationId}/talent-reviews?${params}`,
    { signal }
  );
  return await response.json();
};

/**
 * Get admin notes for a specific location
 */
export const getNotesByLocation = async (locationId: number) => {
  try {
    const response = await fetchWithAuth(
      `${config.api_url}v3/admin/getAdminNotesByTypeAndEntityId`,
      {
        method: "POST",
        body: JSON.stringify({
          type: "Location",
          entityId: locationId
        })
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(
      "Error in getNotesByLocation:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Get talent feedback for jobs at a specific location
 */
export const getFeedbackByLocation = async (locationId: number) => {
  try {
    // Get feedback data with job and hygienist info using the admin paginate endpoint
    const response = await fetchWithAuth(
      `${config.api_url}v3/admin/getPaginateItems`,
      {
        method: "POST",
        body: JSON.stringify({
          tableName: "Feedback",
          limit: 1000,
          page: 1,
          columnNames: [
            "jobId",
            "clinicalSkillsRating",
            "clinicalSkillsDetails",
            "professionalismRating",
            "professionalismDetails",
            "communicationRating",
            "communicationDetails",
            "timeManagementRating",
            "timeManagementDetails",
            "rateSatisfactionRating",
            "rateSatisfactionDetails",
            "additionalFeedbackDetails",
            "createdOn"
          ]
        })
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const allFeedback = await response.json();
    
    // Get jobs for this location to filter feedback
    const jobs = await getJobsByLocation(locationId);
    const jobIds = new Set(jobs.map((job: any) => job.id));
    const locationJobIds = jobs.map((job: any) => ({
      id: job.id,
      hygienistId: job.hygienistId
    }));
    
    // Filter feedback for jobs at this location
    const locationFeedback = allFeedback.filter((feedback: any) =>
      jobIds.has(feedback.jobId)
    );
    
    // Get hygienist info for each feedback
    const feedbackWithTalent = await Promise.all(
      locationFeedback.map(async (feedback: any) => {
        const jobInfo = locationJobIds.find((j: any) => j.id === feedback.jobId);
        const hygienistId = jobInfo?.hygienistId;
        
        if (!hygienistId || hygienistId === 0) {
          return null;
        }
        
        // Get hygienist name
        let hygienistName = "Unknown";
        try {
          const hygResponse = await fetchWithAuth(
            `${config.api_url}v3/admin/hygienist/${hygienistId}`,
            { method: "GET" }
          );
          if (hygResponse.ok) {
            const hygienist = await hygResponse.json();
            hygienistName = `${hygienist.firstName || ""} ${hygienist.lastName || ""}`.trim() || "Unknown";
          }
        } catch (err) {
          console.warn(`Failed to fetch hygienist ${hygienistId}:`, err);
        }
        
        // Calculate average rating from all feedback categories
        const ratings = [
          feedback.clinicalSkillsRating,
          feedback.professionalismRating,
          feedback.communicationRating,
          feedback.timeManagementRating,
          feedback.rateSatisfactionRating
        ].filter((r: any) => r != null && r !== undefined && r !== "");
        
        const averageRating = ratings.length > 0
          ? ratings.reduce((sum: number, r: number) => sum + Number(r), 0) / ratings.length
          : 0;
        
        // Combine all feedback details
        const comments = [
          feedback.clinicalSkillsDetails,
          feedback.professionalismDetails,
          feedback.communicationDetails,
          feedback.timeManagementDetails,
          feedback.rateSatisfactionDetails,
          feedback.additionalFeedbackDetails
        ]
          .filter((c: any) => c != null && c !== undefined && c !== "")
          .join(" | ");
        
        return {
          id: feedback.jobId, // Use jobId as id since Feedback table might not have id
          jobId: feedback.jobId,
          hygienistId,
          hygienistName,
          rating: Math.round(averageRating * 10) / 10, // Round to 1 decimal
          comments: comments || "N/A",
          createdOn: feedback.createdOn
        };
      })
    );
    
    // Filter out null entries and return
    return feedbackWithTalent.filter((f: any) => f !== null);
  } catch (error) {
    console.error(
      "Error in getFeedbackByLocation:",
      error instanceof Error ? error.message : error
    );
    return [];
  }
};

/**
 * Block a talent (hygienist) from working at a specific location
 */
export const blockTalentFromLocation = async (
  locationId: number,
  hygienistId: number
) => {
  try {
    const response = await fetchWithAuth(
      `${config.api_url}v3/admin/blocked`,
      {
        method: "PUT",
        body: JSON.stringify({
          tablename: "BlockedHygienist",
          hygienistId,
          locationId
        })
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(
      "Error in blockTalentFromLocation:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Remove the office document from a location profile
 * Calls the dedicated DELETE endpoint rather than generic updateTable
 */
export const removeLocationDocument = async (locationId: number): Promise<any> => {
  try {
    const res = await fetchWithAuth(
      `${config.api_url}v3/admin/location/${locationId}/document`,
      { method: "DELETE" }
    );
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error(
      "Error in removeLocationDocument:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Reactivate a location (set deactivated = 0).
 *
 * POST /admin/update sets `columnname` to `value` on the `tablename` row `id` and
 * never reads a `change` object (T2-7460). This is the same contract the Location
 * table's DEACTIVATE/REACTIVATE toggle uses. `prevValue` becomes
 * AdminHistory.changedFrom, so pass the location's current `deactivated`.
 */
export const reactivateLocation = async (
  locationId: number,
  previousDeactivated: number
) => {
  try {
    const response = await fetchWithAuth(`${config.api_url}v3/admin/update`, {
      method: "POST",
      body: JSON.stringify({
        tablename: "Location",
        columnname: "deactivated",
        id: locationId,
        value: 0,
        prevValue: previousDeactivated
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error(
      "Error in reactivateLocation:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Simple search function that bypasses the broken dispute search
 * Searches hygienist and location tables directly by individual fields
 */
export const simpleSearch = async (searchData: {
  hygienistId?: number;
  hygienistFirstName?: string;
  hygienistLastName?: string;
  hygienistEmail?: string;
  locationId?: number;
  locationName?: string;
  locationEmail?: string;
  jobId?: number;
}) => {
  try {
    let results: any = {
      hygienists: [],
      locations: [],
      jobs: []
    };

    const fetchAllPaginated = async (tableName: string) => {
      try {
        const items = await getPaginateItems({
          tableName,
          limit: 10000,
          page: 1
        });
        return items || [];
      } catch (error) {
        console.error(`🔍 Error fetching all items for ${tableName}:`, error);
        return [];
      }
    };

    // Search hygienists if any hygienist criteria provided
    if (
      searchData.hygienistId ||
      searchData.hygienistFirstName ||
      searchData.hygienistLastName ||
      searchData.hygienistEmail
    ) {
      console.log("🔍 simpleSearch: Searching hygienist table...");
      const allHygienists = await fetchAllPaginated("Hygienist");
      let filteredHygienists = allHygienists;

      if (searchData.hygienistId) {
        filteredHygienists = filteredHygienists.filter(
          (h: any) => h.id === searchData.hygienistId
        );
      }
      if (searchData.hygienistFirstName) {
        filteredHygienists = filteredHygienists.filter(
          (h: any) =>
            h.firstName &&
            h.firstName
              .toLowerCase()
              .includes(searchData.hygienistFirstName!.toLowerCase())
        );
      }
      if (searchData.hygienistLastName) {
        filteredHygienists = filteredHygienists.filter(
          (h: any) =>
            h.lastName &&
            h.lastName
              .toLowerCase()
              .includes(searchData.hygienistLastName!.toLowerCase())
        );
      }
      // Note: Email for hygienist is on User table, so we can't filter by it here without backend changes.
      // This is a limitation of the current frontend-only approach.

      results.hygienists = filteredHygienists.slice(0, 50); // Limit to 50 results
      console.log(
        `🔍 simpleSearch: Found ${results.hygienists.length} hygienists`
      );
    }

    // Search locations if any location criteria provided
    if (
      searchData.locationId ||
      searchData.locationName ||
      searchData.locationEmail
    ) {
      console.log("🔍 simpleSearch: Searching location table...");
      const allLocations = await fetchAllPaginated("Location");
      let filteredLocations = allLocations;

      if (searchData.locationId) {
        filteredLocations = filteredLocations.filter(
          (l: any) => l.id === searchData.locationId
        );
      }
      if (searchData.locationName) {
        filteredLocations = filteredLocations.filter(
          (l: any) =>
            l.practiceName &&
            l.practiceName
              .toLowerCase()
              .includes(searchData.locationName!.toLowerCase())
        );
      }
      if (searchData.locationEmail) {
        filteredLocations = filteredLocations.filter(
          (l: any) =>
            l.email &&
            l.email
              .toLowerCase()
              .includes(searchData.locationEmail!.toLowerCase())
        );
      }

      results.locations = filteredLocations.slice(0, 50); // Limit to 50 results
      console.log(
        `🔍 simpleSearch: Found ${results.locations.length} locations`
      );
    }

    // Search jobs if job ID provided
    if (searchData.jobId) {
      console.log("🔍 simpleSearch: Searching job table...");
      const allJobs = await fetchAllPaginated("Job");
      let filteredJobs = allJobs;

      if (searchData.jobId) {
        filteredJobs = filteredJobs.filter(
          (j: any) => j.id === searchData.jobId
        );
      }

      results.jobs = filteredJobs.slice(0, 50); // Limit to 50 results
      console.log(`🔍 simpleSearch: Found ${results.jobs.length} jobs`);
    }

    console.log("🔍 simpleSearch: Final results:", results);
    return results;
  } catch (error) {
    console.error("🔍 simpleSearch: General error:", error);
    throw new Error("Search failed. Please try again or contact support.");
  }
};

// Hygienist-related API functions
export const getHygienists = async () => {
  return await fetchWithAuth("v3/admin/hygienist");
};

export const getHygienistById = async (id: number) => {
  return await fetchWithAuth(`v3/admin/hygienist/${id}`);
};

export const updateHygienist = async (id: number, data: any) => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${id}`, {
    method: "PUT",
    body: JSON.stringify(data)
  });
  return ensureOkResponse(response, "Failed to update Talent");
};

export const getJobsByHygienist = async (hygienistId: number) => {
  return await fetchWithAuth(`v3/admin/job?hygienistId=${hygienistId}`);
};

export const getInvoicesByHygienist = async (hygienistId: number) => {
  return await fetchWithAuth(`v3/admin/invoice?hygienistId=${hygienistId}`);
};

export const getPaymentsByHygienist = async (hygienistId: number) => {
  return await fetchWithAuth(`v3/admin/payment?hygienistId=${hygienistId}`);
};

export const getHygienistCancellationsByHygienist = async (
  hygienistId: number
) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/cancellations`
  );
  return await response.json();
};

export const getIgnoredHygienistCancellations = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/ignored-cancellations`
  );
  return await response.json();
};

export interface CancellationReliabilityAdjustment {
  id: number;
  mode: "scored" | "ignored" | "grouped";
  groupedWith?: number | null;
  scoreMode?: "preserve" | "manual" | "auto";
  cancellationScore?: number | null;
  /** T2-6703 — only send when dirty; omit to preserve historical/unknown reasons */
  reason?: string;
  details?: string | null;
}

export interface AdminSourceCorrectionEvidence {
  reason: string;
  evidenceReference: string;
}

export const createAdminSourceCorrectionIdempotencyKey = (scope: string): string => {
  const safeScope = String(scope || "correction")
    .replace(/[^A-Za-z0-9._:-]/g, "-")
    .slice(0, 32);
  const browserCrypto = typeof crypto !== "undefined" ? (crypto as any) : null;
  const randomPart = browserCrypto && typeof browserCrypto.randomUUID === "function"
    ? browserCrypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  return `${safeScope}:${randomPart}`.slice(0, 96);
};

export const updateTalentBlockedUntil = async (
  hygienistId: number,
  payload: { blockedUntil: string | null; reason: string; ticketReference: string },
  idempotencyKey: string
): Promise<{ hygienistId: number; previousBlockedUntil: string | null; blockedUntil: string | null; replayed: boolean }> => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/blocked-until`, {
    method: "PUT",
    headers: { "x-idempotency-key": idempotencyKey },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Could not update Blocked Until (${response.status}).`);
  }
  return response.json();
};

// T2-6367: the reliability-cancellations endpoint now returns the server-sourced
// weighted reliability alongside the cancellation rows so the AP editor renders
// the authoritative weighted score rather than recomputing it client-side.
export interface ReliabilityCancellationsResponse {
  cancellations: any[];
  weightedReliability: number | null;
}

export const getHygienistReliabilityCancellations = async (
  hygienistId: number
): Promise<ReliabilityCancellationsResponse> => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/reliability-cancellations`
  );
  const data = await response.json();
  // Backward-compatible: tolerate the legacy bare-array shape.
  if (Array.isArray(data)) {
    return { cancellations: data, weightedReliability: null };
  }
  return {
    cancellations: Array.isArray(data?.cancellations) ? data.cancellations : [],
    weightedReliability:
      data?.weightedReliability != null ? Number(data.weightedReliability) : null
  };
};

export const saveHygienistReliabilityCancellations = async (
  hygienistId: number,
  adjustments: CancellationReliabilityAdjustment[],
  note: string,
  correction: AdminSourceCorrectionEvidence,
  idempotencyKey: string
) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/reliability-cancellations`,
    {
      method: "PUT",
      headers: { "x-idempotency-key": idempotencyKey },
      body: JSON.stringify({ adjustments, note: note || "", correction })
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText;
    try {
      const parsed = JSON.parse(errorText);
      errorMessage = parsed?.error || parsed?.message || errorText;
    } catch {
      errorMessage = errorText;
    }
    throw new Error(
      errorMessage ||
      `Failed to save cancellation reliability changes: ${response.status}`
    );
  }

  return await response.json();
};

// T2-6651: Talent-profile Office Cancellations (DentistCancelled metadata only)
export interface TalentOfficeCancellation {
  id: number;
  jobId: number;
  shiftDate?: string | null;
  practiceName?: string | null;
  reason?: string | null;
  details?: string | null;
  shortNotice?: number | boolean | null;
  cancelTimestamp?: string | null;
  cancelTimeStamp?: string | null;
  hygienistId?: number | null;
  locationId?: number | null;
}

export const getTalentOfficeCancellations = async (
  hygienistId: number
): Promise<{ cancellations: TalentOfficeCancellation[] }> => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/office-cancellations`
  );
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText;
    try {
      const parsed = JSON.parse(errorText);
      errorMessage = parsed?.error || parsed?.message || errorText;
    } catch {
      errorMessage = errorText;
    }
    throw new Error(
      errorMessage || `Failed to fetch office cancellations: ${response.status}`
    );
  }
  const data = await response.json();
  return {
    cancellations: Array.isArray(data?.cancellations) ? data.cancellations : []
  };
};

export const updateTalentOfficeCancellation = async (
  hygienistId: number,
  cancellationId: number,
  payload: { reason?: string; details?: string; supportNote: string }
) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/office-cancellations/${cancellationId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload)
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText;
    try {
      const parsed = JSON.parse(errorText);
      errorMessage = parsed?.error || parsed?.message || errorText;
    } catch {
      errorMessage = errorText;
    }
    throw new Error(
      errorMessage || `Failed to update office cancellation: ${response.status}`
    );
  }
  return await response.json();
};

export const getHygienistProfile = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/profile`
  );
  return parseRequiredJsonResponse(response, "Failed to fetch Talent profile");
};

export const updateTalentEducationCredentials = async (hygienistId: number, update: EducationUpdate) => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/education-credentials`, {
    method: "PUT", body: JSON.stringify(update)
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(new Error(result.message || "Could not confirm the save. Refresh the profile before trying again."), {
      status: response.status, code: result.code
    });
  }
  return result;
};

export type AdminStripeAccountStatus =
  | "active"
  | "restricted"
  | "pending"
  | "disabled"
  | "not_connected";

export interface AdminStripeAccountSummary {
  connected: boolean;
  status: AdminStripeAccountStatus;
  statusLabel: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirements: Array<{ code: string; label: string; detail?: string }>;
  disabledReason: string | null;
  canRequestInformation: boolean;
}

// T2-7041: Both calls intentionally bypass client-side caching. The API reads
// Stripe live and creates a new single-use account link for every POST.
export const getAdminStripeAccountStatus = async (
  hygienistId: number
): Promise<AdminStripeAccountSummary> => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/stripe-account`,
    { cache: "no-store" }
  );
  return parseRequiredJsonResponse<AdminStripeAccountSummary>(
    response,
    "Failed to load live Stripe account status"
  );
};

export const createAdminStripeRequestInformationLink = async (
  hygienistId: number
): Promise<{ url: string; expiresAt: number | null }> => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/stripe-account/request-information`,
    { method: "POST", cache: "no-store" }
  );
  return parseRequiredJsonResponse<{ url: string; expiresAt: number | null }>(
    response,
    "Failed to create a fresh Stripe Request Information link"
  );
};

export interface StripeExpressAccountRow {
  id: number;
  hygienistId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  stripeAccountId: string;
  country: "CA" | "US";
  countryCode: number;
  status: string;
  reason: string;
  firstDetectedOn: string;
  lastCheckedOn: string;
  lastEmailedOn: string | null;
  lastEmailedByName: string | null;
  canEmailLink: boolean;
  rejected: boolean;
}

// 5.27 FB-4: clear, email-link and recheck each return the one affected row so
// the page patches that row instead of reloading the whole list. `leftList`
// (clear) or `active: false` (recheck) means the row no longer belongs on it.
export interface StripeExpressAccountRowResult {
  row?: StripeExpressAccountRow | null;
  leftList?: boolean;
  active?: boolean;
  cleared?: boolean;
  sent?: boolean;
}

export const listStripeExpressAccounts = async () => {
  const response = await fetchWithAuth("v3/admin/stripe-express-accounts", {
    cache: "no-store"
  });
  return parseRequiredJsonResponse<{ rows: StripeExpressAccountRow[] }>(
    response,
    "Failed to load Stripe Express Accounts"
  );
};

export const clearStripeExpressAccount = async (id: number) => {
  const response = await fetchWithAuth(
    `v3/admin/stripe-express-accounts/${id}/clear`,
    { method: "POST", cache: "no-store" }
  );
  return parseRequiredJsonResponse<StripeExpressAccountRowResult>(
    response,
    "Failed to clear this Stripe Express account row"
  );
};

export const emailStripeExpressFixLink = async (id: number) => {
  const response = await fetchWithAuth(
    `v3/admin/stripe-express-accounts/${id}/email-link`,
    { method: "POST", cache: "no-store" }
  );
  return parseRequiredJsonResponse<StripeExpressAccountRowResult>(
    response,
    "Failed to email the Stripe fix link"
  );
};

// 5.27 FB-4: the explicit per-row Recheck is the only action on this page
// that reads Stripe. Like Clear and Email Link it is keyed by the row (issue)
// ID and returns { rechecked, active, leftList, row }.
export const recheckStripeExpressAccount = async (id: number) => {
  const response = await fetchWithAuth(
    `v3/admin/stripe-express-accounts/${id}/recheck`,
    { method: "POST", cache: "no-store" }
  );
  return parseRequiredJsonResponse<StripeExpressAccountRowResult>(
    response,
    "Failed to recheck this Stripe Express account"
  );
};

export const getUserByHygienist = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `v3/admin/user?hygienistId=${hygienistId}`
  );
  return parseRequiredJsonResponse(response, "Failed to fetch Talent user");
};

export const getNotesByEntity = async (entityId: number, entityType: string) => {
  return await fetchWithAuth(`v3/admin/note?entityId=${entityId}&entityType=${entityType}`);
};

export const createNote = async (data: any) => {
  try {
    const response = await fetchWithAuth(`${config.api_url}v3/admin/addAdminNotes`, {
      method: "POST",
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response;
  } catch (error) {
    console.error(
      "Error in createNote:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const getPotentialDuplicates = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/duplicates`
  );
  return parseRequiredJsonResponse(
    response,
    "Failed to fetch potential duplicates"
  );
};

// Duplicate-hold review flow: global ops queue + per-pair decisions.
export const getDuplicateReviewQueue = async (
  status = "pending",
  scope = "current",
  page = 1,
  limit = 25
) => {
  const response = await fetchWithAuth(
    `v3/admin/duplicates?format=paginated&status=${encodeURIComponent(
      status
    )}&scope=${encodeURIComponent(scope)}&page=${page}&limit=${limit}`
  );
  if (!response.ok) {
    throw new Error("Failed to fetch duplicate review queue");
  }
  return await response.json();
};

export const resolveDuplicate = async (
  duplicateId: number,
  action: "confirm" | "clear"
) => {
  const response = await fetchWithAuth(
    `v3/admin/duplicates/${duplicateId}/resolve`,
    {
      method: "PUT",
      body: JSON.stringify({ action })
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to resolve duplicate pair ${duplicateId}`);
  }
  return await response.json();
};

export const getHygienistJobs = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/jobs`
  );
  return parseRequiredJsonResponse(response, "Failed to fetch Talent jobs");
};

export const getHygienistInvoices = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/invoices`
  );
  return parseRequiredJsonResponse(response, "Failed to fetch Talent invoices");
};

export const getHygienistFeedbackCount = async (
  hygienistId: number
): Promise<number> => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/feedback/count`
  );
  const data = await parseRequiredJsonResponse<{ total?: number }>(
    response,
    "Failed to fetch Talent feedback count"
  );
  return data.total ?? 0;
};

export const getFeedbackWithJobInfo = async (page = 1, limit = 1000): Promise<any[]> => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const response = await fetchWithAuth(`${config.api_url}v3/admin/feedback?${params}`);
  const result = await response.json();
  return Array.isArray(result) ? result : result.data ?? [];
};

export const getHygienistFeedback = async (
  hygienistId: number,
  page: number,
  limit: number,
  sortBy: string,
  sortDesc: boolean,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortDesc: String(sortDesc)
  });
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/feedback?${params}`,
    { signal }
  );
  return parseRequiredJsonResponse<PaginatedResponse>(
    response,
    "Failed to fetch Talent feedback"
  );
};

export const getHygienistNotesDetailed = async (hygienistId: number) => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/notes`
  );
  return parseRequiredJsonResponse(response, "Failed to fetch Talent notes");
};

export const getTalentMembershipHistory = async (
  hygienistId: number,
  page = 1,
  limit = 25
): Promise<MembershipHistoryPage> => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/membership-history?${params}`
  );
  return parseRequiredJsonResponse(response, "Unable to load membership status history.");
};

// T2-6478: Talent-profile editable Skills Feedback + Job Rating (FFS-aware).
export const getHygienistJobRatings = async (
  hygienistId: number,
  page: number,
  limit: number,
  signal?: AbortSignal
): Promise<PaginatedResponse> => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/jobRatings?${params}`,
    { signal }
  );
  return parseRequiredJsonResponse<PaginatedResponse>(
    response,
    "Failed to fetch Talent job ratings"
  );
};

export const updateFeedbackRating = async (
  feedbackId: number,
  field: string,
  value: number,
  correction: AdminSourceCorrectionEvidence,
  idempotencyKey: string
): Promise<any> => {
  const response = await fetchWithAuth(
    `v3/admin/feedback/${feedbackId}/rating`,
    {
      method: "PUT",
      headers: { "x-idempotency-key": idempotencyKey },
      body: JSON.stringify({ field, value, ...correction })
    }
  );
  return parseRequiredJsonResponse(
    response,
    "Failed to update feedback rating"
  );
};

export const updateJobRating = async (
  jobId: number,
  field: string,
  value: number,
  correction: AdminSourceCorrectionEvidence,
  idempotencyKey: string
): Promise<any> => {
  const response = await fetchWithAuth(`v3/admin/job/${jobId}/rating`, {
    method: "PUT",
    headers: { "x-idempotency-key": idempotencyKey },
    body: JSON.stringify({ field, value, ...correction })
  });
  return parseRequiredJsonResponse(response, "Failed to update job rating");
};

/** T2-6896: list per-job fraud rule un-enforce overrides */
export const getJobFraudRuleOverrides = async (jobId: number): Promise<{
  success: boolean;
  jobId: number;
  overrides: Array<{
    id: number;
    jobId: number;
    ruleName: string;
    adminId: number;
    previousScore: number | null;
    isActive: number;
    createdOn: string;
    updatedOn: string;
    clearedOn: string | null;
    clearedByAdminId: number | null;
  }>;
  activeRuleNames: string[];
}> => {
  const response = await fetchWithAuth(`v3/admin/job/${jobId}/fraud-rule-overrides`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to load fraud rule overrides');
  }
  return await response.json();
};

/**
 * T2-6896: un-enforce (unenforced=true) or re-enforce (unenforced=false)
 * a fraud rule for a specific job.
 */
export const setJobFraudRuleOverride = async (
  jobId: number,
  ruleName: string,
  unenforced: boolean
): Promise<{
  success: boolean;
  jobId: number;
  ruleName: string;
  unenforced: boolean;
  total: number;
  components: Array<{ name: string; score: number; ignored?: boolean; rawScore?: number }>;
  activeRuleNames: string[];
}> => {
  const response = await fetchWithAuth(`v3/admin/job/${jobId}/fraud-rule-override`, {
    method: 'PUT',
    body: JSON.stringify({ ruleName, unenforced }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update fraud rule override');
  }
  return await response.json();
};

/**
 * Get comprehensive stats for a hygienist (for admin dashboard "Show Stats" feature)
 * Returns: starScore, ratings breakdown, job stats, cancellation stats, clean slate notes
 */
export interface HygienistStats {
  hygienistId: number;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  starScore: number | null;
  valueGives: number | null;
  totalRatedJobs: number;
  total2StarRatings: number;
  percent2Star: number | null;
  total5StarRatings: number;
  percent5Star: number | null;
  totalJobsCompleted: number;
  totalFutureShifts: number;
  totalCancelledShifts: number;
  cancellationPercentage: number | null;
  cleanSlateNotes: string | null;
  cleanSlateTimestamp: string | null;
  blockedUntil: string | null;
  notes: string | null;
  blacklistReason: string | null;
  upcomingShift: string | null;
  maxBooked: number;
  graduationYear: number | null;
  school: string | null;
  city: string | null;
  province: string | null;
  registerTimestamp: string | null;
  // T2-5698: Full 1-5 star breakdown for the "Show Stats" rating section.
  // Always returns five entries (zero-filled) plus an aggregate total.
  ratingBreakdown: RatingBreakdownBucket[];
  totalRatings: number;
  // T2-6373: FFS 90-day recent feedback window buckets for the Show Stats panel.
  ffsRecentRatings: RatingBreakdownBucket[];
  ffsRecentRatingsTotal: number;
  ffsRecentRatingsWindowLabel: string;
  // Compatibility aliases returned by the API for older admin clients.
  recentRatingBreakdown: RatingBreakdownBucket[] | null;
  recentTotalRatings: number;
  // T2-6363: read-only Fair Fresh Start investigation block.
  fairFreshStart?: FairFreshStart | null;
}

export interface RatingBreakdownBucket {
  stars: 1 | 2 | 3 | 4 | 5;
  count: number;
}

// T2-6363/6379: read-only Fair Fresh Start membership snapshot surfaced in the
// "Show Stats" dialog (Talent profile) and the disputes Stats dialog.
export interface FairFreshStart {
  status: {
    currentStatusCode: string | null;
    rawStatusCode: string | null;
    displayLabel?: string | null;
    statusCategory?: string | null;
    reliabilityLabel?: string | null;
    feedbackLabel?: string | null;
    reliabilityPercentile?: number | null;
    feedbackPercentile?: number | null;
    completedShifts?: number | null;
    ratedShifts?: number | null;
    grandfathering?: {
      active: boolean;
      until: string | null;
      group: string | null;
      protectedStatusCode: string | null;
      calculatedStatusCode: string | null;
    } | null;
  };
  reliability: {
    weightedReliability: number | null;
    percentile: number | null;
    completedShifts: number | null;
  };
  feedback: {
    weightedFeedbackScore: number | null;
    percentile: number | null;
    ratedShifts: number | null;
    feedbackCount?: number | null;
    feedbackCount90Days?: number | null;
    overallFeedbackScore?: number | null;
    recentFeedbackScore?: number | null;
    lastFeedbackCalculation?: string | null;
  };
  grandfathering: {
    isActive: boolean;
    protectedStatusCode: string | null;
    calculatedStatusCode?: string | null;
    grandfatheredUntil: string | null;
    daysRemaining: number | null;
  };
  recoveryState: {
    overlayState: string | null;
    onABreakUntil: string | null;
    sourceSystem: string | null;
    targetRestrictionProfile: string | null;
  };
  thresholds: {
    reliability: Record<string, number>;
    feedback: Record<string, number>;
    completedShifts: Record<string, number>;
    ratedShifts: Record<string, number>;
  };
}

export const getHygienistStats = async (hygienistId: number): Promise<HygienistStats> => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/stats`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch hygienist stats: ${response.status}`);
  }
  return await response.json();
};

// T2-6397: read-only Courses section on the Talent profile. The API resolves
// required/recommended courses for the Talent's current FFS membership state and
// returns live TalentLMS progress (with a synced-mirror fallback).
export interface HygienistCourse {
  code: string | null;
  name: string;
  requirementType: "required" | "recommended" | string;
  status: "not_started" | "in_progress" | "completed" | "passed" | string;
  assignedOn: string | null;
  completedOn: string | null;
  reason: string | null;
}

export const getHygienistCourses = async (
  hygienistId: number
): Promise<HygienistCourse[]> => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/courses`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to fetch hygienist courses: ${response.status}`
    );
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export interface CourseProgressResetResult {
  reset: boolean;
  auditId: number;
  course: HygienistCourse;
}

export const resetHygienistCourseProgress = async (
  hygienistId: number,
  courseCode: string
): Promise<CourseProgressResetResult> => {
  const response = await fetchWithAuth(
    `v3/admin/hygienist/${hygienistId}/courses/${encodeURIComponent(courseCode)}/reset`,
    { method: "POST", body: JSON.stringify({ confirmed: true }) }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      (typeof errorData.error === "string" && errorData.error) ||
      errorData.error?.message ||
      errorData.message ||
      `Failed to reset course progress: ${response.status}`;
    throw new Error(message);
  }
  return response.json();
};

// T2-6420: admin-editable Job Board partner (Recruit North America) link URL.
const extractApiError = (errorData: any, fallback: string): string => {
  const err = errorData && errorData.error;
  if (typeof err === "string") return err;
  if (err && typeof err.message === "string") return err.message;
  if (errorData && typeof errorData.message === "string") return errorData.message;
  return fallback;
};

export const getJobBoardPartnerLink = async (): Promise<string> => {
  const response = await fetchWithAuth(`v3/admin/appSettings/jobBoardPartnerLink`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      extractApiError(
        errorData,
        `Failed to fetch Job Board partner link: ${response.status}`
      )
    );
  }
  const data = await response.json();
  return (data && data.url) || "";
};

export const updateJobBoardPartnerLink = async (url: string): Promise<string> => {
  const response = await fetchWithAuth(`v3/admin/appSettings/jobBoardPartnerLink`, {
    method: "PUT",
    body: JSON.stringify({ url })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      extractApiError(
        errorData,
        `Failed to update Job Board partner link: ${response.status}`
      )
    );
  }
  const data = await response.json();
  return (data && data.url) || url;
};

// T2-6905 Feature Flag Control Panel
export interface FeatureFlagMode {
  key: string;
  enabled: 0 | 1;
  value: string | null;
  description: string;
}

export interface FeatureFlagHazard {
  level: "standard" | "hazardous";
  direction?: "on" | "off" | "any";
  blastStatement?: string;
}

export interface FeatureFlagLastChange {
  when: string;
  adminEmail: string;
  note: string | null;
}

export interface FeatureFlagItem {
  key: string;
  label: string;
  category: string;
  control: "switch" | "modes" | "date";
  modes?: FeatureFlagMode[];
  description: string;
  impactOn?: string;
  impactOff?: string;
  hazard: FeatureFlagHazard;
  rolledOut?: boolean;
  inert?: boolean;
  relatedEpic?: string;
  enabled: 0 | 1 | null;
  value: string | null;
  modeKey?: string | null;
  status: "ok" | "drift" | "missing-row" | "unregistered" | "inert";
  expectedForEnv?: { enabled: 0 | 1; value: string | null } | null;
  lastChange: FeatureFlagLastChange | null;
  unregistered?: boolean;
  missingRow?: boolean;
}

export interface FeatureFlagsResponse {
  environment: string;
  canWrite: boolean;
  flags: FeatureFlagItem[];
}

export interface FeatureFlagHistoryEntry {
  id: number;
  flagName: string;
  oldEnabled: 0 | 1 | null;
  oldValue: string | null;
  newEnabled: 0 | 1;
  newValue: string | null;
  oldStateLabel?: string | null;
  newStateLabel?: string | null;
  adminEmail: string;
  note: string | null;
  source: string;
  createdOn: string;
  emailedAllowlist?: boolean;
}

const featureFlagRequest = async (
  path: string,
  options: RequestInit = {}
): Promise<any> => {
  const response = await fetchWithAuth(`v3/admin/feature-flags${path}`, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({} as any));
    const err: any = new Error(
      extractApiError(errorData, `HTTP ${response.status}`)
    );
    err.status = response.status;
    throw err;
  }
  return response.json();
};

export const getFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
  return featureFlagRequest("");
};

export const getFeatureFlagHistory = async (
  name: string
): Promise<FeatureFlagHistoryEntry[]> => {
  const body = await featureFlagRequest(`/${encodeURIComponent(name)}/history`);
  return body.history || [];
};

export const updateFeatureFlag = async (
  name: string,
  body: { enabled?: 0 | 1; value?: string | null; note?: string; confirmation?: string }
): Promise<FeatureFlagItem> => {
  return featureFlagRequest(`/${encodeURIComponent(name)}`, {
    method: "PUT",
    body: JSON.stringify(body)
  });
};

/**
 * Location stats interface for admin dashboard "Show Stats" feature
 */
export interface LocationStats {
  locationId: number;
  practiceName: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  status: string;
  notes: string | null;
  registerTimestamp: string | null;
  totalJobsCompleted: number;
  totalRatedJobs: number;
  total2StarRatings: number;
  percent2Star: number;
  total5StarRatings: number;
  percent5Star: number;
  nonPaymentReportsCount: number;
  nonPaymentPercentage: number;
  totalCancelled: number;
  cancellationPercentage: number;
}

export const getLocationStats = async (locationId: number): Promise<LocationStats> => {
  const response = await fetchWithAuth(`v3/admin/location/${locationId}/stats`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch location stats: ${response.status}`);
  }
  return await response.json();
};

export const applyMembershipAction = async (hygienistId: number, action: string) => {
  return await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/membershipAction`, {
    method: "POST",
    body: JSON.stringify({ action })
  });
};

export interface ManualBreakPayload {
  durationMonths: 0 | 3 | 6 | number;
  note: string;
  evidenceReference: string;
}

export interface ManualNotAFitPayload {
  note: string;
  ticketReference: string;
  reasonCategory?: "credential_review" | "administrative_block";
}

const parseManualMembershipResponse = async (response: Response, fallback: string) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || fallback);
  }
  return data;
};

export const applyManualBreak = async (
  hygienistId: number,
  payload: ManualBreakPayload,
  idempotencyKey: string
) => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/take-a-break`, {
    method: "POST",
    headers: { "x-idempotency-key": idempotencyKey },
    body: JSON.stringify(payload)
  });
  return parseManualMembershipResponse(response, "Failed to update Take a Break");
};

export const applyManualNotAFit = async (
  hygienistId: number,
  payload: ManualNotAFitPayload,
  idempotencyKey: string
) => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/not-a-fit`, {
    method: "POST",
    headers: { "x-idempotency-key": idempotencyKey },
    body: JSON.stringify(payload)
  });
  return parseManualMembershipResponse(response, "Failed to apply Not a Fit");
};

export const undoManualNotAFit = async (
  hygienistId: number,
  payload: ManualNotAFitPayload,
  idempotencyKey: string
) => {
  const response = await fetchWithAuth(`v3/admin/hygienist/${hygienistId}/not-a-fit/undo`, {
    method: "POST",
    headers: { "x-idempotency-key": idempotencyKey },
    body: JSON.stringify(payload)
  });
  return parseManualMembershipResponse(response, "Failed to undo manual Not a Fit");
};

export const adminLoginWithIdToken = async (
  idToken: string
): Promise<AdminLoginResponse & { isAgent: boolean }> => {
  console.log("🔑 Admin OAuth Login: Attempting login via Cognito id token");
  console.log("🔑 Admin OAuth Login: Token length:", idToken.length);

  const response = await fetch(`${config.api_url}v3/admin/oauth-login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${idToken}`
    }
  });

  console.log("🔑 Admin OAuth Login: Response status:", response.status);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const failure = adminOAuthFailureFromResponse(errorData, response.status);
    throw new AdminOAuthLoginError(failure);
  }

  const data = await response.json();
  console.log("🔑 Admin OAuth Login: Success! Token received");
  console.log("🔑 Admin OAuth Login: Admin data:", data.admin);
  return data;
};

export const searchEmails = async (data: { query: string; dateFrom?: string; dateTo?: string; limit?: number }) => {
  try {
    const response = await fetchWithAuth(`${config.api_url}v3/admin/email/search`, {
      method: "POST",
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error searching emails:", error);
    throw error;
  }
};

const parseBucketEmailResponse = async <T>(
  response: Response,
  fallback: string
): Promise<T> => {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.message || fallback), {
      reason: body.reason,
      retryAt: body.retryAt
    });
  }
  return response.json();
};

export const getBucketEmails = async (
  params: BucketEmailListParams
): Promise<BucketEmailListResponse> => {
  const query = new URLSearchParams();
  query.set("pageSize", String(params.pageSize));
  if (params.query) query.set("query", params.query);
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  if (params.cursor) query.set("cursor", params.cursor);
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/bucket-email?${query.toString()}`,
    { method: "GET" }
  );
  return parseBucketEmailResponse(response, "Failed to load Bucket Email");
};

export const getBucketEmailHealth = async (): Promise<BucketEmailHealth> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/bucket-email/health`,
    { method: "GET" }
  );
  return parseBucketEmailResponse(
    response,
    "Failed to load Bucket Email sync status"
  );
};

export const getBucketEmailDetail = async (
  messageId: string
): Promise<BucketEmailDetail> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/bucket-email/${encodeURIComponent(messageId)}`,
    { method: "GET" }
  );
  return parseBucketEmailResponse(response, "Failed to load this Bucket email");
};

export const downloadBucketEmailAttachment = async (
  messageId: string,
  attachmentId: string
): Promise<Blob> => {
  const response = await fetchWithAuth(
    `${config.api_url}v3/admin/bucket-email/${encodeURIComponent(
      messageId
    )}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: "GET" }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || "Failed to download this attachment");
  }
  return response.blob();
};

export const syncCoursesToDatabase = async () => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/courses/sync`, {
      method: "POST"
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(
        `Failed to sync courses: ${res.status} ${res.statusText} - ${errorText}`
      );
    }

    return await res.json();
  } catch (error) {
    console.error(
      "Error in syncCoursesToDatabase:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

export const triggerCronJob = async (jobName: string) => {
  try {
    let res = await fetchWithAuth(`${config.api_url}v3/admin/cron/trigger`, {
      method: "POST",
      body: JSON.stringify({ jobName })
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
    }
    
    return await res.json();
  } catch (error) {
    console.error(
      "Error in triggerCronJob:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

// ============================================================================
// Mobile Build Triggers (admin "Trigger iOS/Android Build" buttons)
// ============================================================================

export type BuildPlatform = "ios" | "android";
export type BuildEnvironment =
  | "develop"
  | "staging"
  | "pre-merge-production"
  | "production";

export interface MobileBuildDispatchResponse {
  success: boolean;
  platform: BuildPlatform;
  environment: BuildEnvironment;
  branch: string;
  workflow: string;
  runId: number | null;
  runUrl: string;
  status: string;
  createdAt: string;
}

export interface MobileBuildStep {
  name: string;
  status: "queued" | "in_progress" | "completed" | string;
  conclusion: "success" | "failure" | "cancelled" | "skipped" | null;
  number: number;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface MobileBuildJob {
  id: number;
  name: string;
  status: "queued" | "in_progress" | "completed" | string;
  conclusion: "success" | "failure" | "cancelled" | "skipped" | null;
  startedAt?: string | null;
  completedAt?: string | null;
  steps: MobileBuildStep[];
}

export interface MobileBuildStatusResponse {
  id: number;
  status: "queued" | "in_progress" | "completed" | string;
  conclusion: "success" | "failure" | "cancelled" | "skipped" | null;
  runUrl: string;
  createdAt: string;
  updatedAt: string;
  event: string;
  displayTitle?: string;
  name?: string;
  headBranch?: string;
  jobs: MobileBuildJob[];
}

export const triggerMobileBuild = async (
  platform: BuildPlatform,
  environment: BuildEnvironment,
  validateOnly = false,
  marketingVersion?: string,
  buildNumber?: string
): Promise<MobileBuildDispatchResponse> => {
  const body: Record<string, unknown> = { platform, environment, validateOnly };
  if (marketingVersion) body.marketingVersion = marketingVersion;
  if (buildNumber) body.buildNumber = buildNumber;

  const res = await fetchWithAuth(`${config.api_url}v3/admin/build/trigger`, {
    method: "POST",
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
  }
  return await res.json();
};

export const getMobileBuildStatus = async (
  runId: number
): Promise<MobileBuildStatusResponse> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/build/status/${runId}`
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
  }
  return await res.json();
};

export const getRecentMobileBuild = async (
  platform: BuildPlatform
): Promise<{ run: MobileBuildStatusResponse | null }> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/build/recent/${platform}`
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
  }
  return await res.json();
};

export const cancelMobileBuild = async (
  runId: number
): Promise<{ success: boolean; runId: number }> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/build/cancel/${runId}`,
    { method: "POST" }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
  }
  return await res.json();
};

export const getStoreLatestVersion = async (
  platform: BuildPlatform
): Promise<{ platform: BuildPlatform; version: string | null; configured: boolean }> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/build/store-version/${platform}`
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
  }
  return await res.json();
};

// ============================================================================
// Partner Referral Codes API Functions
// ============================================================================

export type PromotionType = 'free_time_period' | 'free_placements';

export interface PartnerReferralCode {
  id: number;
  partner_name: string;
  referral_code: string;
  promotion_type: PromotionType;
  free_days?: number;
  free_placements?: number;
  active_duration_days: number | null;
  email_template: string | null;
  valid_from: string | null;
  valid_until: string | null;
  notes: string | null;
  created_at: string;
}

type CreatePayloadCommon = {
  partner_name: string;
  referral_code: string;
  active_duration_days?: number | null;
  email_template?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  notes?: string | null;
};

export type CreatePartnerReferralCodePayload = CreatePayloadCommon & (
  | { promotion_type: 'free_time_period'; free_days: number; free_placements?: never }
  | { promotion_type: 'free_placements'; free_placements: number; free_days?: never }
);

type UpdatePayloadCommon = {
  partner_name?: string;
  referral_code?: string;
  active_duration_days?: number | null;
  email_template?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  notes?: string | null;
};

export type UpdatePartnerReferralCodePayload = UpdatePayloadCommon & (
  | { promotion_type: 'free_time_period'; free_days?: number; free_placements?: never }
  | { promotion_type: 'free_placements'; free_placements?: number; free_days?: never }
);

/**
 * Get all partner referral codes
 */
export const getPartnerReferralCodes = async (): Promise<PartnerReferralCode[]> => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/partner-referral-codes`, {
      method: "GET"
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
    }
    
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getPartnerReferralCodes:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Create a new partner referral code
 */
export const createPartnerReferralCode = async (
  data: CreatePartnerReferralCodePayload
): Promise<{ success: boolean; id: number; message: string }> => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/partner-referral-codes`, {
      method: "POST",
      body: JSON.stringify(data)
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
    }
    
    return await res.json();
  } catch (error) {
    console.error(
      "Error in createPartnerReferralCode:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Update an existing partner referral code
 */
export const updatePartnerReferralCode = async (
  id: number,
  data: UpdatePartnerReferralCodePayload
): Promise<{ success: boolean; message: string }> => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/partner-referral-codes/${id}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
    }
    
    return await res.json();
  } catch (error) {
    console.error(
      "Error in updatePartnerReferralCode:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Delete a partner referral code
 */
export const deletePartnerReferralCode = async (
  id: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/partner-referral-codes/${id}`, {
      method: "DELETE"
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
    }
    
    return await res.json();
  } catch (error) {
    console.error(
      "Error in deletePartnerReferralCode:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

// ============================================================================
// Mandrill Email Templates API
// ============================================================================

export interface MandrillTemplate {
  slug: string;
  name: string;
  publish_name: string;
}

/**
 * Get all available Mandrill email templates
 */
export const getMandrillTemplates = async (): Promise<MandrillTemplate[]> => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/email-templates`, {
      method: "GET"
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
    }

    return await res.json();
  } catch (error) {
    console.error(
      "Error in getMandrillTemplates:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

// ============================================================================
// Database Switching API Functions (Staging Only, Developer Role Only)
// ============================================================================

export interface DatabaseSwitchStatus {
  canUseDatabaseSwitch: boolean;
  currentEnvironment: string;
  adminRole: number | null;
  isDeveloper: boolean;
  isStaging: boolean;
  availableSources: DatabaseSource[];
  message: string;
}

export interface DatabaseConnectionTest {
  success: boolean;
  message: string;
  source: DatabaseSource;
  testQuery?: string;
  warning?: string;
}

/**
 * Get the database switch status for the current admin
 * Returns whether database switching is available and current settings
 */
export const getDatabaseSwitchStatus = async (): Promise<DatabaseSwitchStatus> => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/database-switch/status`, {
      method: "GET"
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
    }
    
    return await res.json();
  } catch (error) {
    console.error(
      "Error in getDatabaseSwitchStatus:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

/**
 * Test connection to the specified database
 * @param source - The database source to test ('staging' or 'prod-replica')
 */
export const testDatabaseConnection = async (source: DatabaseSource): Promise<DatabaseConnectionTest> => {
  try {
    const res = await fetchWithAuth(`${config.api_url}v3/admin/database-switch/test-connection?source=${source}`, {
      method: "GET"
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
    }
    
    return await res.json();
  } catch (error) {
    console.error(
      "Error in testDatabaseConnection:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};

// ─── Partner Affiliation ──────────────────────────────────────────────────────

export interface PartnerAffiliationStatus {
  id: number;
  practiceName: string;
  isCentralBooked: number | null;
  centralBookedId: string | null;
  stripeCustomerId: string | null;
  partnerName: "123Dentist" | "DentalCorp" | null;
}

export const getPartnerAffiliation = async (
  locationId: number
): Promise<PartnerAffiliationStatus> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/partner-affiliation/${locationId}`
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
  }
  return await res.json();
};

export const setPartnerAffiliation = async (data: {
  locationId: number;
  partner: 1 | 2;
  centralBookedId?: string;
  previewToken?: string;
  idempotencyKey?: string;
}): Promise<PartnerAffiliationResult> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/set-partner-affiliation`,
    { method: "POST", body: JSON.stringify(data) }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error(errorData.error || `HTTP error! status: ${res.status}`), {
      status: res.status,
      code: errorData.code
    });
  }
  return await res.json();
};

export interface PartnerConversionShift {
  id: number;
  startDate: string;
  postedStart: string | null;
  status: number;
  type: number;
  paymentTerms: number | null;
  paymentMethod: number | null;
  outcome: "posted" | "booked" | "review" | "unchanged";
  reason: string;
}
export interface PartnerAffiliationPreview {
  location: { id: number; practiceName: string };
  previewToken: string;
  shifts: PartnerConversionShift[];
  counts: { posted: number; booked: number; review: number; unchanged: number };
}
export interface PartnerAffiliationResult {
  message: string;
  location: PartnerAffiliationStatus;
  shifts?: PartnerConversionShift[];
  counts?: PartnerAffiliationPreview["counts"];
  replayed?: boolean;
}
export const previewPartnerAffiliation = async (data: {
  locationId: number;
  centralBookedId: string;
}): Promise<PartnerAffiliationPreview> => {
  const res = await fetchWithAuth(`${config.api_url}v3/admin/partner-affiliation-preview`, {
    method: "POST", body: JSON.stringify(data)
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
  }
  return res.json();
};

export const removePartnerAffiliation = async (
  locationId: number
): Promise<{ message: string; locationId: number; previousPartner: string }> => {
  const res = await fetchWithAuth(
    `${config.api_url}v3/admin/remove-partner-affiliation`,
    { method: "POST", body: JSON.stringify({ locationId }) }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
  }
  return await res.json();
};

// ─── Support Assistant ───────────────────────────────────────────────────────

const SUPPORT_ASSISTANT_PATH = "v3/admin/support-assistant/conversations";

export const getSupportAssistantConversations = async (): Promise<SupportAssistantConversationListResponse> => {
  const response = await fetchWithAuth(SUPPORT_ASSISTANT_PATH);
  return parseRequiredJsonResponse<SupportAssistantConversationListResponse>(
    response,
    "Could not load support conversations"
  );
};

export const createSupportAssistantConversation = async (
  title?: string
): Promise<CreateSupportAssistantConversationResponse> => {
  const response = await fetchWithAuth(SUPPORT_ASSISTANT_PATH, {
    method: "POST",
    body: JSON.stringify(title ? { title } : {})
  });
  return parseRequiredJsonResponse<CreateSupportAssistantConversationResponse>(
    response,
    "Could not start a support conversation"
  );
};

export const getSupportAssistantConversation = async (
  conversationId: string
): Promise<SupportAssistantConversationResponse> => {
  const response = await fetchWithAuth(
    `${SUPPORT_ASSISTANT_PATH}/${encodeURIComponent(conversationId)}`
  );
  return parseRequiredJsonResponse<SupportAssistantConversationResponse>(
    response,
    "Could not load the support conversation"
  );
};

export const sendSupportAssistantMessage = async (
  conversationId: string,
  message: string
): Promise<SendSupportAssistantMessageResponse> => {
  const response = await fetchWithAuth(
    `${SUPPORT_ASSISTANT_PATH}/${encodeURIComponent(conversationId)}/messages`,
    {
      method: "POST",
      body: JSON.stringify({ message })
    }
  );
  return parseRequiredJsonResponse<SendSupportAssistantMessageResponse>(
    response,
    "Could not send the support question"
  );
};

export const archiveSupportAssistantConversation = async (
  conversationId: string
): Promise<void> => {
  const response = await fetchWithAuth(
    `${SUPPORT_ASSISTANT_PATH}/${encodeURIComponent(conversationId)}`,
    { method: "DELETE" }
  );
  if (!response.ok) {
    await parseRequiredJsonResponse(
      response,
      "Could not archive the support conversation"
    );
  }
};
